import { api, ApiError } from "./client";
import type { BulkBuyerActionItemDto } from "./buyerActions";
import type { PrequalInviteDto, PrequalSubmissionCompletionMode } from "./prequalification";
import type { DocumentDto, PagedResult } from "@/types/api";
import type { CombinedIntroMode } from "@/lib/buyerCombinedIntroRules";

// Re-exported so the buyer surfaces import one module. The DTO itself stays
// owned by the prequalification wire mirror — a second hand-maintained copy is
// exactly what the shared status vocabulary was created to stop.
export type { PrequalInviteDto };

// Wire mirror of the Buyer CRM read/write contracts. The C# enums are
// string-stored, so these unions are the literal values on the wire — a value
// added server-side must be added here too or it renders as a raw token.

export type BuyerLeadStatus =
  | "New"
  | "Assigned"
  | "AttemptingContact"
  | "Engaged"
  | "Qualified"
  | "ActivelySubmitting"
  | "ConvertedToBorrower"
  | "ClosedPurchased"
  | "NotQualified"
  | "Nurture"
  | "Dead";

export type BuyerSubmissionStatus =
  | "Shortlisted"
  | "BuyerApproved"
  | "OutreachSent"
  | "BrokerResponded"
  | "PackageGenerated"
  | "PackageSent"
  | "InfoReceived"
  | "CallMeeting"
  | "OfferLoi"
  | "UnderContract"
  | "ClosedPurchased"
  | "EscalatedToDeal"
  | "DeclinedByBuyer"
  | "NoResponse"
  | "RejectedByBroker"
  | "Passed"
  | "Dead";

export type QualificationVerdict = "Unknown" | "Pass" | "Concern" | "Fail";

export type BuyerLeadSource =
  | "Unknown"
  | "PaidSocial"
  | "PaidSearch"
  | "Organic"
  | "Referral"
  | "PrequalificationForm"
  | "InboundCall"
  | "InboundEmail"
  | "Event"
  | "PurchasedList"
  | "BusinessBroker"
  | "ManualEntry"
  | "YouTubeLandingPage"
  | "ListingSite"
  | "Other";

export type BuyerAssignmentMethod = "Manual" | "Bulk" | "AutoDistribute";

/** Why a buyer row needs the specialist's attention. Urgency order is the
 *  server's; the string is the enum member name on the wire. */
export type BuyerNeedsActionReason =
  | "FollowUpOverdue"
  | "SubmissionAwaitingBuyer"
  | "NoContact"
  | "FollowUpDueToday"
  | "NewAssignment"
  | "QualifiedNoSubmission";

// ---------------------------------------------------------------------------
// Buyers
// ---------------------------------------------------------------------------

export interface BuyerLeadSummaryDto {
  /** The prequalification site that created this buyer, and its owner — the
   *  "via" line. Null when there is none; see `createdViaLabel`. */
  createdViaSite?: string | null;
  createdViaSiteOwner?: string | null;
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  displayName: string;
  /** How a staff list row or heading names the buyer: "{Company} — {Person}"
   *  for a company buyer (`BuyerAcquirer.StaffDisplayName`), else
   *  `displayName`. Chips, task labels, folder names and email "To" defaults
   *  keep `displayName` (ruling Q5). Always sent by the server; optional so
   *  hand-built fixtures compile — read it as `staffDisplayName ?? displayName`. */
  staffDisplayName?: string;
  email?: string | null;
  phone?: string | null;
  city?: string | null;
  state?: string | null;
  status: BuyerLeadStatus;
  source: BuyerLeadSource;
  assignedSpecialistUserId?: string | null;
  assignedSpecialistName?: string | null;
  liquidCapital?: number | null;
  maxPurchasePrice?: number | null;
  overallVerdict: QualificationVerdict;
  submissionCount: number;
  /** DERIVED from the furthest-along submission (L5); null with no submissions. */
  furthestSubmissionStatus?: BuyerSubmissionStatus | null;
  lastContactAtUtc?: string | null;
  // 🔴 `nextFollowUpDate` is GONE from the buyer (it lives on WorkTasks now —
  // see `nextActionDueAt` below). The submission's follow-up date is a
  // different column and is untouched.
  createdAt: string;
  /** Server-resolved. Never re-derive the ownership rule client-side. */
  canEdit: boolean;
  convertedPartyId?: string | null;
  /**
   * Why this row needs the specialist's attention, in urgency order (the
   * server's `BuyerNeedsActionReason`). Never null; empty when nothing does.
   * Optional on the type only so existing fixtures keep compiling.
   */
  needsActionReasons?: BuyerNeedsActionReason[];
  /** The soonest open follow-up task's due instant, and its title. */
  nextActionDueAt?: string | null;
  nextActionTitle?: string | null;
  /** The advertisement this lead is attributed to, if any. */
  advertisementId?: string | null;
  advertisementName?: string | null;
  /** The buyer this row is suspected to duplicate — stamped when the public
   *  funnel created it automatically and the dedupe probe found a match. The
   *  funnel creates rather than linking on an email match, so the duplicate a
   *  manager merges is the accepted cost of that choice; it is only containment
   *  if the queue shows it. */
  suspectedDuplicateOfBuyerLeadId?: string | null;
  /** The suspected match's display name — "possible duplicate of Dana Whitfield"
   *  is actionable in a way "possible duplicate" is not. */
  suspectedDuplicateOfName?: string | null;
  /** Only ever true when the caller asked for archived rows — the default list
   *  excludes them. On the row rather than inferred from the filter, because a
   *  filtered list with no per-row marker is ambiguous the moment anyone links
   *  into it or forgets which view they are on. */
  isArchived?: boolean;
  /** The team whose queue this lead belongs to (WS2). 🔴 NOT the same question
   *  as who is working it: a specialist may hold a lead belonging to the team
   *  they have since left (D2), so this and `assignedSpecialistUserId` can name
   *  different teams. The queue's Team column renders this. */
  buyerTeamId?: string | null;
  /** Resolved team name — a column showing a GUID is not one anybody can act
   *  on. Null when the lead is unrouted. */
  buyerTeamName?: string | null;
  /**
   * Whether the CALLER may change who owns this row — reassign it or return it
   * to the queue. Server-resolved by `BuyerReassignmentRule`: a
   * `Buyers.ManageAll` holder for a lead on a team they dispatch, or the row's
   * current owner (who may hand it to a teammate). The list renders the
   * specialist cell as a control on `true` and as plain text otherwise; the
   * server enforces the same rule regardless, so this can only ever hide a
   * control, never widen one. Optional on the type so existing fixtures keep
   * compiling; the server always sends it.
   */
  canReassign?: boolean;
  /**
   * Whether the CALLER may return this row to the queue — the gate the
   * unassign endpoint enforces (`BuyerReassignmentRule.CanUnassign`: a manager
   * on the OWNER's team, else the owner). The specialist cell offers
   * "Unassigned (queue)" on `true`, beside the hand-off roster it reads once per
   * page. Optional for the same reason as `canReassign`.
   */
  canUnassign?: boolean;
}

/**
 * The standing assessment — now a PROJECTION of the newest completed
 * qualification interview, and a HISTORICAL row for buyers assessed before the
 * interview existed.
 *
 * <p>🔴 <b>Read-only on every surface.</b> The five-dropdown write path is
 * retired: there is no `upsertQualification`, and changing an assessment means
 * conducting an interview. `financing` survives because historical rows carry
 * one — the dimension is gone from the model, and nothing new writes it.</p>
 *
 * <p>There is deliberately no `computedVerdict`: the computed-versus-overridden
 * pair now lives on the interview's own outcome, which is where the rules that
 * produced it live too.</p>
 */
export interface BuyerLeadQualificationDto {
  id: string;
  buyerLeadId: string;
  financialCapacity: QualificationVerdict;
  financialCapacityNote?: string | null;
  geography: QualificationVerdict;
  geographyNote?: string | null;
  experience: QualificationVerdict;
  experienceNote?: string | null;
  timeline: QualificationVerdict;
  timelineNote?: string | null;
  financing: QualificationVerdict;
  financingNote?: string | null;
  overallVerdict: QualificationVerdict;
  isOverridden: boolean;
  overrideReason?: string | null;
  overriddenAtUtc?: string | null;
  overriddenByUserId?: string | null;
  assessedAtUtc?: string | null;
  assessedByUserId?: string | null;
}

// ---------------------------------------------------------------------------
// The buyer qualification interview
//
// 🔴 The QUESTIONS ARE SERVED, never mirrored here. The registry is the
// interview — its questions, their frozen option keys and their branch
// conditions — and the server's derivation rules match on those very keys. A
// hand-maintained TypeScript copy is a second definition free to drift, and the
// copy that drifts is the one nobody is looking at. So this file types the
// SHAPE of the registry and nothing of its content.
// ---------------------------------------------------------------------------

/** How an answer is shaped. `one` = a radio group, `many` = checkboxes,
 *  `free` = a textarea with no options. */
export type BuyerInterviewAnswerKind = "one" | "many" | "free";

export type BuyerInterviewStatus = "InProgress" | "Completed";

export interface BuyerInterviewSectionDto {
  id: string;
  title: string;
  purpose: string;
}

export interface BuyerInterviewOptionDto {
  key: string;
  label: string;
}

/** The condition that reveals a deep-dive question: the parent carries any of
 *  `whenAnyOf`. `label` is shown as the reason the branch opened — the
 *  interview explaining itself, never a rating hint. */
/** One branch condition: the parent question and the option keys that open it. */
export interface BuyerInterviewConditionDto {
  parentQuestionId: string;
  whenAnyOf: string[];
}

export interface BuyerInterviewBranchDto {
  parentQuestionId: string;
  whenAnyOf: string[];
  label: string;
  /** An optional SECOND parent that must also carry one of its keys (owner
   *  ruling Q9, option B — e.g. MF3b: not managing it full time AND a company is
   *  buying). */
  alsoRequires?: BuyerInterviewConditionDto | null;
}

export interface BuyerInterviewQuestionDto {
  id: string;
  sectionId: string;
  label: string;
  kind: BuyerInterviewAnswerKind;
  /** Counts toward completion. Coverage is measured against these alone. */
  spine: boolean;
  /** Record it in their words — this answer can travel into the profile letter. */
  verbatim: boolean;
  hint?: string | null;
  /** When set, the question carries a free-text box BESIDE its choices. */
  textBoxLabel?: string | null;
  options: BuyerInterviewOptionDto[];
  branch?: BuyerInterviewBranchDto | null;
  /** Retired from the live interview (the one-call brief, D2): never asked in a
   *  new interview, but a past interview's stored answer still renders. */
  retired?: boolean;
  /** Where the answer comes from on the unified call. `derived`: not asked, it
   *  comes from the call's prequalification answers. `partlyDerived`: only
   *  `callAskedOptionKeys` (and the text box) are asked. `derivedWhenAnswered`:
   *  asked unless the server lists it in `derivedQuestionIds`. */
  callSource?: BuyerInterviewCallSource;
  callAskedOptionKeys?: string[] | null;
}

export type BuyerInterviewCallSource =
  | "none" | "derived" | "partlyDerived" | "derivedWhenAnswered";

export interface BuyerInterviewRegistryDto {
  sections: BuyerInterviewSectionDto[];
  questions: BuyerInterviewQuestionDto[];
  spineCount: number;
  /** The wording version this registry is rendered in; 0 = the coded
   *  defaults. */
  wordingVersion?: number;
}

/** One recorded answer. An empty `choices` clears the selection; a blank `text`
 *  clears the box — a blank IS "nothing recorded". */
export interface BuyerInterviewAnswerDto {
  choices: string[];
  text?: string | null;
}

/** Per-section progress. COVERAGE ONLY — how many of the section's spine
 *  questions are recorded. It says nothing whatsoever about the answers. */
export interface BuyerInterviewSectionProgressDto {
  sectionId: string;
  spineAnswered: number;
  spineTotal: number;
}

/** The six dimensions, as the server names them. Financing is deliberately
 *  absent: that picture lives in the prequalification system. */
export type BuyerQualificationDimension =
  | "FinancialCapacity"
  | "Geography"
  | "Experience"
  | "Timeline"
  | "DecisionAuthority"
  | "MotivationFit";

export interface BuyerInterviewDimensionDto {
  dimension: BuyerQualificationDimension;
  verdict: QualificationVerdict;
  ruleId: string;
  /** The rule that produced the verdict, in words. Rendered beside it — a
   *  verdict whose reasoning is hidden is one nobody can argue with. */
  ruleText: string;
  /** The answers the rule that fired read directly (server-side rule table).
   *  Empty or absent for a Pass, for `incomplete`, and for a last-resort rule
   *  whose own branch reads no answer. Never derived in the UI. */
  questionIds?: string[] | null;
}

export interface BuyerInterviewFlagDto {
  code: string;
  text: string;
  /** As on {@link BuyerInterviewDimensionDto.questionIds}. */
  questionIds?: string[] | null;
}

/** The thresholds in force when this interview was completed, so a historical
 *  interview explains itself in the numbers of its own day. */
export interface BuyerInterviewThresholdsDto {
  passRatio: number;
  concernRatio: number;
  knockoutRatio: number;
  implausiblePriceFloor: number;
  workingCapitalRatio: number;
}

export interface BuyerInterviewOutcomeDto {
  dimensions: BuyerInterviewDimensionDto[];
  knockouts: BuyerInterviewFlagDto[];
  coherenceFlags: BuyerInterviewFlagDto[];
  computedVerdict: QualificationVerdict;
  overriddenVerdict?: QualificationVerdict | null;
  effectiveVerdict: QualificationVerdict;
  overrideReason?: string | null;
  overriddenAtUtc?: string | null;
  overriddenByUserId?: string | null;
  downPaymentRatio?: number | null;
  thresholds: BuyerInterviewThresholdsDto;
  /** The overrider's display name, resolved server-side. Null when there is no
   *  override or the overrider has no display name. */
  overriddenByUserName?: string | null;
}

/**
 * A conducted interview.
 *
 * <p>🔴 <b>`outcome` IS ABSENT until the interview is completed</b> — not
 * merely hidden by the client. While one is in progress the payload carries no
 * verdict, no score, no rule, no knockout and no coherence flag, so no
 * component can render a rating hint that would steer what the specialist
 * writes down. `progress` is coverage, and is safe.</p>
 */
export interface BuyerInterviewDto {
  id: string;
  buyerLeadId: string;
  version: number;
  status: BuyerInterviewStatus;
  startedAtUtc: string;
  completedAtUtc?: string | null;
  conductedByUserId?: string | null;
  conductedByUserName?: string | null;
  /** Keyed by question id. Absent means nothing recorded. */
  answers: Record<string, BuyerInterviewAnswerDto>;
  progress: BuyerInterviewSectionProgressDto[];
  spineComplete: boolean;
  missingSpineQuestionIds: string[];
  outcome?: BuyerInterviewOutcomeDto | null;
  /** Question ids whose answer was derived from the call's prequalification
   *  answers rather than recorded directly. */
  derivedQuestionIds?: string[] | null;
  /** The wording version a COMPLETED interview was conducted with (0 = the
   *  coded defaults, also for interviews completed before the wording editor).
   *  Null while in progress — the live interview renders the current wording. */
  wordingVersion?: number | null;
  /** In progress only: a wording version was published after this interview
   *  started (the drawer shows a one-line notice and refetches). */
  wordingChangedSinceStart?: boolean;
  /** In progress only: the wording version in force now. The drawer refetches
   *  the current wording whenever what it shows differs. */
  currentWordingVersion?: number | null;
}

/** One row of the version history. */
export interface BuyerInterviewSummaryDto {
  id: string;
  version: number;
  status: BuyerInterviewStatus;
  startedAtUtc: string;
  completedAtUtc?: string | null;
  conductedByUserId?: string | null;
  conductedByUserName?: string | null;
  effectiveVerdict?: QualificationVerdict | null;
  isOverridden: boolean;
}

/** Autosave ONE answer — a dropped call must lose nothing, and a real
 *  interview does not follow the section order. */
export interface SaveBuyerInterviewAnswerPayload {
  questionId: string;
  choices: string[];
  text?: string | null;
}

/** Override the computed roll-up on a COMPLETED interview. The reason is
 *  required server-side; the computed value is never overwritten. */
export interface OverrideBuyerInterviewVerdictPayload {
  verdict: QualificationVerdict;
  reason: string;
}

// ── Company & sponsor buyers (owner brief 2026-10-08) ─────────────────────
// Mirrors Core/Enums/Buyers/BuyerCompanyEnums.cs (string-valued on the wire);
// pinned by test/prequalEnumUnionsMatchTheServer.test.ts. Null buyer type is
// "not asked" and reads as an individual everywhere.
export type BuyerType = "Individual" | "CompanyOwnedOrLed" | "InvestmentGroupOrFund";
export type BuyerEquitySource = "CompanyFundsOnHand" | "CommittedByInvestors" | "StillBeingRaised";
export type BuyerOperatorArrangement =
  | "CompanyPrincipal"
  | "HiredGeneralManager"
  | "ExistingManagement"
  | "DesignatedPerson";

/** The client mirror of `BuyerAcquirer.IsCompanyBuyer`: a company type AND a
 *  non-blank company name. A company type with no name is an individual (Q11). */
export function isCompanyBuyer(type: BuyerType | null | undefined, companyName: string | null | undefined): boolean {
  return (type === "CompanyOwnedOrLed" || type === "InvestmentGroupOrFund") && !!companyName?.trim();
}

export interface BuyerLeadDetailDto {
  /** The prequalification site that created this buyer, and its owner — the
   *  "via" line. Null when there is none; see `createdViaLabel`. */
  createdViaSite?: string | null;
  createdViaSiteOwner?: string | null;
  // Acquiring company (company & sponsor buyers).
  buyerType?: BuyerType | null;
  acquiringCompanyName?: string | null;
  acquiringCompanyTitle?: string | null;
  acquiringCompanyWebsite?: string | null;
  acquiringCompanyAbout?: string | null;
  equitySource?: BuyerEquitySource | null;
  operatorArrangement?: BuyerOperatorArrangement | null;
  operatorName?: string | null;
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  displayName: string;
  /** How a staff list row or heading names the buyer: "{Company} — {Person}"
   *  for a company buyer (`BuyerAcquirer.StaffDisplayName`), else
   *  `displayName`. Chips, task labels, folder names and email "To" defaults
   *  keep `displayName` (ruling Q5). Always sent by the server; optional so
   *  hand-built fixtures compile — read it as `staffDisplayName ?? displayName`. */
  staffDisplayName?: string;
  email?: string | null;
  phone?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  assignedSpecialistUserId?: string | null;
  assignedSpecialistName?: string | null;
  assignedAtUtc?: string | null;
  status: BuyerLeadStatus;

  // Milestones (L6) — stamps, never derived from status.
  firstContactAttemptAtUtc?: string | null;
  firstLiveConversationAtUtc?: string | null;
  prequalCompletedAtUtc?: string | null;
  pfsReceivedAtUtc?: string | null;
  proofOfFundsVerifiedAtUtc?: string | null;
  qualifiedAtUtc?: string | null;

  liquidCapital?: number | null;
  netWorth?: number | null;
  maxPurchasePrice?: number | null;
  downPaymentAvailable?: number | null;
  creditScoreBand?: number | null;

  targetIndustries?: string | null;
  targetGeographies?: string | null;
  targetRevenueMin?: number | null;
  targetRevenueMax?: number | null;
  targetCashFlowMin?: number | null;
  timelineToPurchase?: string | null;

  industryExperience?: string | null;
  managementExperience?: string | null;
  isSbaCandidate?: boolean | null;
  financingNotes?: string | null;
  relocationWillingness?: string | null;

  source: BuyerLeadSource;
  sourceDetail?: string | null;

  convertedPartyId?: string | null;
  convertedAtUtc?: string | null;
  convertedByUserId?: string | null;

  lastContactAtUtc?: string | null;
  notes?: string | null;

  /** Retained so a mis-extraction is always recoverable by a human reading the
   *  original paste. */
  rawPastedText?: string | null;
  capturedLinksJson?: string | null;
  aiModel?: string | null;

  createdAt: string;
  modifiedAt?: string | null;
  submissionCount: number;
  furthestSubmissionStatus?: BuyerSubmissionStatus | null;
  qualification?: BuyerLeadQualificationDto | null;
  canEdit: boolean;
  /** PR 2d. Null when the buyer has neither a saved block nor any pasted
   *  source text. Optional on the type so the existing fixtures keep
   *  compiling. */
  biography?: BuyerLeadBiographyDto | null;

  /** Archived. The detail page renders a persistent banner off this — the
   *  record still opens and stays editable, so the banner is the only thing
   *  telling a reader it is hidden everywhere else. Optional so the existing
   *  fixtures keep compiling. */
  isArchived?: boolean;
  archivedAtUtc?: string | null;
  /** Already resolved to a display name — a banner showing a raw GUID tells a
   *  reader nothing they can act on. */
  archivedByName?: string | null;
  archiveReason?: string | null;

  /** When the education email was first sent. Null = not yet, which the
   *  Qualification tab renders as an outstanding step rather than a blank. */
  educationSentAtUtc?: string | null;
  educationSentByName?: string | null;
  /** Null when never sent; true when the specialist sent it themselves. */
  educationSentSelfAttested?: boolean | null;

  /** The advertisement this lead is attributed to — the ONE join key every
   *  advertising figure is computed over. Stamped by the matcher on
   *  site-originated leads, picked by hand on the staff intake paths. */
  advertisementId?: string | null;
  /** Resolved server-side so the detail page never renders a GUID and never
   *  has to read the advertising surface (which an F2 cannot) to label it. */
  advertisementName?: string | null;
  advertisementChannel?: string | null;
  advertisementTeamName?: string | null;
  /** The team whose queue this lead belongs to. 🔴 NOT who is working it
   *  (D2). On an assign-at-create the ASSIGNEE's team wins here while the ad's
   *  credit rolls up under the AD's team (Q5) — the two may differ. */
  buyerTeamId?: string | null;
  buyerTeamName?: string | null;
  /** Same rule as `BuyerLeadSummaryDto.canReassign`. */
  canReassign?: boolean;
  /** Same rule as `BuyerLeadSummaryDto.canUnassign`. */
  canUnassign?: boolean;
  /** 🔴 CREATE-RESPONSE ONLY. True when the assignee named on a create had no
   *  team, so the row's team came from the ordinary router instead (Q4 B).
   *  Every read path leaves it false; the form uses it once to say
   *  "assigned to X; team set by routing". */
  teamSetByRouting?: boolean;
  /** D6/D15 — the SAME reasons / next task the list row carries, computed by
   *  the server from the same batch facts and the buyer's OWN team threshold,
   *  so the header strip and the list can never disagree. Always an array on
   *  the wire (the server coalesces); optional here, as on the summary, so the
   *  hand-written detail fixtures keep compiling. */
  needsActionReasons?: BuyerNeedsActionReason[];
  nextActionDueAt?: string | null;
  nextActionTitle?: string | null;
}

/** Archive a buyer. */
export interface ArchiveBuyerLeadPayload {
  /** Required, min 10 characters. */
  reason: string;
  /** Also move the buyer to NotQualified, through the ordinary status path.
   *  Optional — archiving is available on ANY status, because a buyer nobody
   *  ever statused is exactly the record cluttering the list. */
  alsoSetNotQualified?: boolean;
}

export interface BulkArchiveBuyerLeadsPayload {
  ids: string[];
  /** One reason for the batch. Per-row reasons would mean a form nobody fills
   *  in honestly. */
  reason: string;
}

export interface BulkArchiveBuyerLeadsResultDto {
  requested: number;
  archived: number;
  /** Skipped as already archived — not a failure. */
  alreadyArchived: number;
  notFound: number;
  /** Per-row outcome, so a partial result names WHICH rows did not archive.
   *  Optional on the type: a server predating it sends none. */
  items?: BulkBuyerActionItemDto[];
}

/**
 * The education email's preview state for one buyer.
 *
 * `body` does NOT include Phoenix's role statement: the transport appends that
 * to every counterparty email, below anything composed here, and it is
 * unreachable from this surface. Showing it in the preview would suggest it
 * was editable.
 */
export interface BuyerEducationEmailStateDto {
  buyerLeadId: string;
  buyerName: string;
  /** The RESOLVED address, stated literally rather than as "the address on
   *  file". Null when the buyer has none. */
  toEmail?: string | null;
  subject: string;
  body: string;
  /** False when there is no address, or no buyer brochure is configured — the
   *  education email does not go without it. A brochure that exists but is
   *  momentarily unreadable does NOT clear this: that is a retry, and it
   *  surfaces from the send instead. */
  canSend: boolean;
  blockedReason?: string | null;
  sentAtUtc?: string | null;
  sentByName?: string | null;
  sentSelfAttested?: boolean | null;
  /** The live Buyer brochure's file name, so the confirmation can name what it
   *  is about to attach. Null when none is configured — which is also the
   *  blocked state. */
  brochureFileName?: string | null;
  brochureSizeBytes?: number | null;
}

/** A time-limited read link to the program brochure. `available: false` is
 *  DATA — a fresh environment has no brochure — never an error. */
export interface ProgramBrochureLinkDto {
  role: string;
  available: boolean;
  url?: string | null;
  expiresAtUtc?: string | null;
  fileName?: string | null;
}

export interface SendBuyerEducationEmailResultDto {
  toEmail: string;
  subject: string;
  sentAtUtc: string;
  /** False when the email went but the Communications row did not. */
  communicationLogged: boolean;
  state: BuyerEducationEmailStateDto;
}

/** Partial edit of a buyer. Every field optional; omitted means unchanged. */
export interface RecordNdaPayload {
  signedAtUtc?: string | null;
  sentToBrokerAtUtc?: string | null;
  ndaDocumentId?: string | null;
  authorizationNote?: string | null;
  notRequiredReason?: string | null;
}

// ---------------------------------------------------------------------------
// Versioned paperwork records (WS4)
//
// A record is a STACK OF VERSIONS, newest first, each owning its own dates, its
// own document and its own dated notes. One shape serves every kind — the kind
// is a field rather than two DTO families, because every difference between an
// NDA and a prequalification is in the words a surface renders.
// ---------------------------------------------------------------------------

export type BuyerRecordKind = "Nda" | "Prequalification";

/**
 * Where a version came from — and therefore how far its ATTRIBUTION can be
 * trusted.
 *
 * <p>🔴 `RecordedBeforeVersioning` means the migration carried this in from the
 * flat NDA columns. <b>Its dates are true; its authorship is not.</b> The old
 * `NdaSignedByUserId` was assigned with `=` rather than `??=`, so it holds the
 * LAST person to re-record rather than the original signer, and nothing in the
 * old schema distinguished the two. A surface showing a name for one of these
 * must say what it is rather than asserting "signed by".</p>
 */
export type BuyerRecordVersionOrigin = "Recorded" | "RecordedBeforeVersioning";

/**
 * One dated, attributed note on one version.
 *
 * <p>🔴 <b>`canEdit` is computed SERVER-SIDE by the same rule the write path
 * enforces</b> — true only for the caller's own most recent entry on the
 * version, and only when they may write the record at all. Never recompute it
 * here: a locally-derived flag is how a surface comes to offer an action the
 * service refuses.</p>
 */
export interface BuyerRecordVersionEntryDto {
  id: string;
  versionId: string;
  /** When the thing being noted HAPPENED — back-datable, and what the thread
   *  orders on. Not the same as `createdAt`, which is when it was typed. */
  occurredAtUtc: string;
  body: string;
  authorUserId?: string | null;
  authorName?: string | null;
  createdAt: string;
  modifiedAt?: string | null;
  canEdit: boolean;
}

/**
 * One version — a complete snapshot, not a delta.
 *
 * <p>🔴 <b>Not single-outcome.</b> `signedAtUtc`, `sentAtUtc` and
 * `notRequiredReason` are INDEPENDENT and may all hold at once. A surface must
 * render them as three facts and never resolve them into one status: "no NDA
 * was required" and "one arrived anyway" are both true, and the second does not
 * make the first a mistake.</p>
 */
export interface BuyerRecordVersionDto {
  id: string;
  versionNumber: number;
  isCurrent: boolean;
  origin: BuyerRecordVersionOrigin;
  recordedAtUtc: string;
  signedAtUtc?: string | null;
  sentAtUtc?: string | null;
  notRequiredReason?: string | null;
  documentId?: string | null;
  signedByUserId?: string | null;
  signedByUserName?: string | null;
  entries: BuyerRecordVersionEntryDto[];
}

/**
 * One paperwork record in full.
 *
 * <p>`versions` is newest-first and INCLUDES `current`, so a timeline never has
 * to splice two collections back together in the right order. `current` is
 * derived server-side from the highest live version number — there is no stored
 * current flag.</p>
 */
export interface BuyerRecordDto {
  kind: BuyerRecordKind;
  buyerSubmissionId?: string | null;
  buyerLeadId?: string | null;
  current?: BuyerRecordVersionDto | null;
  versions: BuyerRecordVersionDto[];
  canWrite: boolean;
}

/** Adds a note to a version. `occurredAtUtc` defaults to now server-side;
 *  back-dating is allowed and expected — a call on Tuesday logged on Thursday
 *  should read Tuesday. */
export interface AddBuyerRecordEntryPayload {
  occurredAtUtc?: string | null;
  body: string;
}

/** Edits a note. Permitted only where the entry's own `canEdit` says so. */
export interface UpdateBuyerRecordEntryPayload {
  occurredAtUtc?: string | null;
  body: string;
}

/**
 * Partial edit of a buyer. Bare `undefined` means UNCHANGED — the FIND-12
 * convention this codebase uses everywhere — so a form that edits one field
 * sends one field. Follow-ups are no longer a column on the buyer — they are
 * WorkTasks, scheduled through `buyerActionsApi.scheduleFollowUp`.
 *
 * <p>Until this PR only `source` / `sourceDetail` were declared, because
 * `LeadSourceField` was the only writer that existed anywhere in the product.
 * The server has always accepted the whole shape.</p>
 */
export interface UpdateBuyerLeadPayload {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  // Acquiring company (company & sponsor buyers).
  buyerType?: BuyerType | null;
  acquiringCompanyName?: string | null;
  acquiringCompanyTitle?: string | null;
  acquiringCompanyWebsite?: string | null;
  acquiringCompanyAbout?: string | null;
  equitySource?: BuyerEquitySource | null;
  operatorArrangement?: BuyerOperatorArrangement | null;
  operatorName?: string | null;

  source?: BuyerLeadSource;
  sourceDetail?: string | null;

  liquidCapital?: number | null;
  netWorth?: number | null;
  maxPurchasePrice?: number | null;
  downPaymentAvailable?: number | null;
  creditScoreBand?: number | null;

  targetIndustries?: string | null;
  targetGeographies?: string | null;
  targetRevenueMin?: number | null;
  targetRevenueMax?: number | null;
  targetCashFlowMin?: number | null;
  timelineToPurchase?: string | null;

  industryExperience?: string | null;
  managementExperience?: string | null;
  isSbaCandidate?: boolean | null;
  financingNotes?: string | null;
  relocationWillingness?: string | null;

  // The campaign code and the nine UTM/ad fields that used to sit here were
  // retired (D2): the ADVERTISEMENT picker below is the attribution control.

  /**
   * Re-attribute the lead to an advertisement — or, with
   * `clearAdvertisementId`, to none. Validated server-side against the caller's
   * advertising scope. Picking an advertisement DEFAULTS `source` from its
   * channel (`sourceForChannel` in `@/lib/advertisingChannelSources`).
   */
  advertisementId?: string | null;

  notes?: string | null;

  // -------------------------------------------------------------------------
  // Clear flags
  // -------------------------------------------------------------------------
  //
  // 🔴 A nullable field cannot express its own erasure under FIND-12, because
  // bare `null` already means "leave unchanged". A string sidesteps that by
  // sending `""`. A NUMBER cannot — there is no empty number — and neither can a
  // tri-state `bool?`, where "the buyer never said" is a real answer distinct
  // from `false`. So until these existed, blanking a figure on this record was
  // simply unexpressible over the API: the request was accepted, and the old
  // value silently stayed.
  //
  // Same shape as `UpdateUserRequest.ClearAvatar` and
  // `UpdateLoanFeeRequest.ClearRevenuePool`. One flag per non-string field, so
  // the set is derivable rather than remembered.
  clearLiquidCapital?: boolean;
  clearNetWorth?: boolean;
  clearMaxPurchasePrice?: boolean;
  clearDownPaymentAvailable?: boolean;
  clearCreditScoreBand?: boolean;
  clearTargetRevenueMin?: boolean;
  clearTargetRevenueMax?: boolean;
  clearTargetCashFlowMin?: boolean;
  clearIsSbaCandidate?: boolean;
  clearAdvertisementId?: boolean;
  clearBuyerType?: boolean;
  clearEquitySource?: boolean;
  clearOperatorArrangement?: boolean;
}

/**
 * The patch's clear flags, as a key union.
 *
 * Derived from the interface by its `clear` prefix rather than listed, so adding
 * a flag above cannot leave this — or the create payload's exclusion below — out
 * of date.
 */
export type BuyerClearFlagKey = Extract<keyof UpdateBuyerLeadPayload, `clear${string}`>;

/**
 * Create payload. The edit field set, plus the provenance a create can record
 * and an edit cannot meaningfully re-set.
 *
 * <p>🔴 `rawPastedText` / `aiModel` are what make a mis-extraction RECOVERABLE:
 * `BuyerLeadDetailDto` reads the original paste back precisely so a human can
 * see what the model was given. A paste-to-create flow that drops them leaves
 * that recovery path empty, and the record silently becomes the model's
 * summary with no way back to the source.</p>
 *
 * <p>🔴 `confirmCreateDespiteDuplicate` records that a human SAW the duplicate
 * warning and chose to continue. This module's rule is detect, warn, never
 * block — so the acknowledgement is the only artefact that distinguishes a
 * considered decision from an unnoticed collision.</p>
 *
 * <p>`assignToSpecialistUserId` is CREATE-ONLY (Q3). It used to be deliberately
 * undeclared so no form could bypass the queue; the server now writes the same
 * grant-ledger row and owner stamp the queue's assign path writes, so a buyer
 * assigned at creation is indistinguishable in the ledger from one placed out
 * of the queue — which is what made it safe to declare. The server still
 * refuses an assignee the caller may not assign to, so the picker
 * (`buyerAssignmentsApi.candidates()`) is what to offer, never a free list.</p>
 */
export interface CreateBuyerLeadPayload
  extends Omit<UpdateBuyerLeadPayload, BuyerClearFlagKey> {
  /** Defaults to `Unknown` server-side when omitted — or, when
   *  `advertisementId` is set and this is not, to the advertisement's channel
   *  source. */
  source?: BuyerLeadSource;

  /**
   * Assign at creation. Omitted lands the buyer UNASSIGNED in the queue. The
   * assignee's team becomes the row's team (Q5); an assignee on NO team never
   * fails the capture — the row is routed as an unassigned one would be and the
   * response says so via `teamSetByRouting` (Q4 B).
   */
  assignToSpecialistUserId?: string | null;

  /** The text the specialist pasted, verbatim. */
  rawPastedText?: string | null;
  /** Which model read it — pairs with `rawPastedText`, never sent alone. */
  aiModel?: string | null;
  /** Anything link-shaped the extractor found, as stored JSON. */
  capturedLinksJson?: string | null;

  confirmCreateDespiteDuplicate?: boolean;
}

export interface SetBuyerLeadStatusPayload {
  status: BuyerLeadStatus;
  reason?: string | null;
}

export interface BuyerLeadDuplicateDto {
  id: string;
  /** The matched buyer's name as the server words it (`DisplayNameFor`: name →
   *  email → phone). The server never sent first/last names here, so a type that
   *  declared them made every warning fall back to the email or phone. */
  displayName: string;
  email?: string | null;
  phone?: string | null;
  status: BuyerLeadStatus;
  assignedSpecialistUserId?: string | null;
  assignedSpecialistName?: string | null;
  createdAt: string;
  /** "email" or "phone". Shown so the specialist can judge — an email match on
   *  a shared family address is much weaker evidence than a phone match. */
  matchedOn: string;
}

export interface BuyerLeadDuplicateCheckDto {
  hasMatches: boolean;
  matches: BuyerLeadDuplicateDto[];
}

/** What the AI read out of a pasted buyer inquiry. A PREFILL, never a create
 *  request — every field is nullable and nothing is validated. */
export interface BuyerLeadExtractionResultDto {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;

  liquidCapital?: number | null;
  netWorth?: number | null;
  maxPurchasePrice?: number | null;
  downPaymentAvailable?: number | null;

  targetIndustries?: string | null;
  targetGeographies?: string | null;
  targetRevenueMin?: number | null;
  targetRevenueMax?: number | null;
  targetCashFlowMin?: number | null;
  timelineToPurchase?: string | null;

  industryExperience?: string | null;
  managementExperience?: string | null;
  isSbaCandidate?: boolean | null;
  financingNotes?: string | null;
  relocationWillingness?: string | null;

  source: BuyerLeadSource;
  sourceDetail?: string | null;

  /** Everything in the paste that maps to no field above. Nothing is dropped. */
  notesSummary?: string | null;
  modelUsed?: string | null;

  /** Who is buying — set only when the paste clearly shows the person acting
   *  for a company whose name appears in the paste verbatim; otherwise null
   *  with all three company members null. Never `Individual`. A prefill the
   *  specialist confirms or clears. */
  buyerType?: BuyerType | null;
  acquiringCompanyName?: string | null;
  acquiringCompanyTitle?: string | null;
  acquiringCompanyWebsite?: string | null;
}

// The freeform "Send email" payload and result DTOs stood here.
//
// 🔴 REMOVED, with the endpoint behind them. The system sends exactly three
// emails a specialist composes — the listing intro, the submission package, and
// the prequalification invitation with its nudge — and each is a template with a
// preview, a recipient the record already knows, and a recorded send. A fourth
// door that took an arbitrary subject and body bypassed every one of those
// properties: nothing checked who it went to, nothing rendered what it would
// look like, and the resulting record said only that *some* mail was sent.
//
// `IBuyerOutreachService` on the server is NOT removed — it is the transport the
// intro email runs on.

/**
 * Convert to Borrower.
 *
 * <p>Supply `existingPartyId` to LINK an existing borrower, or omit it to MINT
 * one. `email` corrects the address the party is created with without rewriting
 * the buyer row — the buyer keeps what it has forever.</p>
 */
export interface ConvertBuyerLeadPayload {
  existingPartyId?: string | null;
  email?: string | null;
  overrideReason?: string | null;
}

export interface ConvertBuyerLeadResultDto {
  partyId: string;
}

/**
 * A month of a volume series.
 *
 * 🔴 `month` is a `yyyy-MM` WALL-CALENDAR key, not an instant. Format it with
 * `monthLabel` (`pages/buyers/indexTiles/tileBits`), never by constructing a
 * `Date` and running it through the timezone-aware formatter — that renders June
 * as May for every reader west of Greenwich.
 */
export interface MonthlyCountPoint {
  month: string;
  count: number;
}

export interface BuyerLeadStatsDto {
  total: number;
  unassigned: number;
  qualified: number;
  activelySubmitting: number;
  converted: number;
  /** Open follow-up tasks due strictly before the Phoenix day. */
  overdueFollowUp: number;
  byStatus: Record<string, number>;
  bySource: Record<string, number>;
  /** Rows carrying at least one `needsActionReasons` entry. */
  needsAction?: number;
  /** Open follow-up tasks due within the Phoenix day. */
  followUpDueToday?: number;
  /** Attention — live buyers nobody has spoken to inside the window. */
  noContactRecently: number;
  /** The window `noContactRecently` was counted over, in days (D5 #10). The
   *  list prefers this for its label and its drill-through, so the count and
   *  the rows behind it ride ONE number the server owns. Optional: a server
   *  that omits it leaves the list on its own mirror of the constant. */
  noContactDays?: number;
  /** Attention — cleared qualification, never presented to a listing. */
  qualifiedNoSubmission: number;
  /** What the qualified half of this filter set can spend. Reads the
   *  `qualifiedAtUtc` STAMP, so capacity is not revoked when a qualified buyer
   *  is later parked. */
  qualifiedCapacity: number;
  /** Conversion denominator — ASSIGNED buyers. Both rates divide by this one
   *  number, which is what stops two cards answering the same question
   *  irreconcilably. */
  assignedTotal: number;
  assignedQualified: number;
  assignedConverted: number;
  createdByMonth: MonthlyCountPoint[];
}

export interface BuyerLeadListParams {
  page?: number;
  pageSize?: number;
  q?: string;
  status?: BuyerLeadStatus[];
  source?: BuyerLeadSource[];
  assignedSpecialistUserId?: string;
  /** The manager's queue. Distinct from an absent specialist filter. */
  unassigned?: boolean;
  mineOnly?: boolean;
  /** Narrow to one team's leads (`BuyerLead.buyerTeamId`) — the team QUEUE
   *  tab's table reads the ordinary list with this set. A filter, never the
   *  authorization: the read path is open to every internal role (D5). */
  buyerTeamId?: string;
  overallVerdict?: QualificationVerdict[];
  /** Open follow-up task overdue (strictly before the Phoenix day). */
  overdueFollowUpOnly?: boolean;
  /** Rows with any needs-action reason / a follow-up due today. */
  needsActionOnly?: boolean;
  dueTodayOnly?: boolean;
  /**
   * Created at or after this instant — the "new this week" view.
   *
   * 🔴 DECLARED AHEAD OF THE SERVER. `BuyerLeadListQuery` does not carry it
   * yet (reported for the integration pass); until it does the server ignores
   * the param and the view degrades to its `newest` sort.
   */
  createdAfter?: string;
  convertedOnly?: boolean;
  /** Attention drill-throughs. Each shares its server-side expression with the
   *  tile that counted it, so a pill and its table cannot disagree. */
  noContactWithinDays?: number;
  qualifiedNoSubmissionOnly?: boolean;
  /**
   * The reversible archive, as a TRI-STATE.
   *
   * Undefined (the default) lists un-archived buyers. `true` lists ONLY the
   * archived ones — that is what makes an archived buyer reachable again
   * without knowing its id. A boolean could not express that, so archiving
   * would be a one-way trip through the UI even though unarchive exists.
   *
   * The stat tiles always describe the working book, whichever view the table
   * is showing.
   */
  archived?: boolean;
}

// ---------------------------------------------------------------------------
// Listings
// ---------------------------------------------------------------------------

export interface ListingSummaryDto {
  id: string;
  businessName?: string | null;
  headline?: string | null;
  industry?: string | null;
  city?: string | null;
  state?: string | null;
  marketplaceName?: string | null;
  rawUrl?: string | null;
  askingPrice?: number | null;
  cashFlow?: number | null;
  revenue?: number | null;
  listingAgentId?: string | null;
  listingAgentName?: string | null;
  listingAgentFirmName?: string | null;
  submissionCount: number;
  createdAt: string;
  /** Addendum B (B1): the agent as every table shows it. */
  listingAgent?: ListingAgentBriefDto | null;
  /** The listing's canonical label — the server's one ladder (business name →
   *  headline → the listing URL's host → "Untitled listing"). OPTIONAL on the
   *  wire so inline test literals and cached payloads minted before it existed
   *  keep type-checking; read it through `listingNameOrFallback`. */
  displayName?: string;
}

/** A listing's agent as a list row shows it (Addendum B, B1). `label` is the
 *  label every agent surface uses ("Firm contact — {Firm}" for a firm contact). */
export interface ListingAgentBriefDto {
  id: string;
  label: string;
  firmName?: string | null;
  email?: string | null;
  phone?: string | null;
  isFirmContact: boolean;
}

export interface ListingAgentDto {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  displayName: string;
  email?: string | null;
  phone?: string | null;
  firmName?: string | null;
  title?: string | null;
  city?: string | null;
  state?: string | null;
  brokerId?: string | null;
  /** Set once this agent has been escalated into broker recruitment — the ONLY
   *  seam this module opens into the Leads subsystem. */
  escalatedToLeadId?: string | null;
  escalatedAtUtc?: string | null;
  escalatedByUserId?: string | null;
  notes?: string | null;
  listingCount: number;
  createdAt: string;
  /** The display name of this agent's most recent listing, so two agents who
   *  share a name can be told apart in a picker. Null when they have none.
   *  OPTIONAL on the wire so inline fixtures minted before it keep compiling. */
  sampleListingName?: string | null;
  /** What kind of line `phone` is (D20). OPTIONAL on the wire so fixtures
   *  minted before it keep compiling; absent reads as "Unknown". */
  phoneType?: ListingAgentPhoneType;
  /** A firm's general contact with no named person (D19). When true the
   *  server's `displayName` already reads "Firm contact — {Firm}". */
  isFirmContact?: boolean;
}

/** Mirrors `ListingAgentPhoneType` on the server (string-serialised). */
export type ListingAgentPhoneType = "Unknown" | "Mobile" | "Office";

export const LISTING_AGENT_PHONE_TYPES: { value: ListingAgentPhoneType; label: string }[] = [
  { value: "Unknown", label: "Unknown" },
  { value: "Mobile", label: "Mobile" },
  { value: "Office", label: "Office" },
];

/** One agent the duplicate check found, and which details it matched on. */
export interface ListingAgentDuplicateMatchDto {
  agent: ListingAgentDto;
  /** `"email"`, `"phone"`, `"name"` — see {@link LISTING_AGENT_MATCH_FIELDS}. */
  matchedOn: string[];
  /** True when a create with these details would REUSE this agent instead of
   *  inserting one, which is why `canCreateNew` is then false. */
  reusedByCreate: boolean;
}

/**
 * What a New agent form shows before it creates anything.
 *
 * <p>🔴 <code>canCreateNew</code> is answered by the SAME probe the create runs
 * (`ListingAgentService.CreateCandidatesAsync` on the server), so a form that
 * offers CREATE NEW ANYWAY offers it only when the create would really insert
 * a row. Branch on it; never re-derive it from `matches`.</p>
 */
export interface ListingAgentDuplicateCheckDto {
  matches: ListingAgentDuplicateMatchDto[];
  createWouldReuse: boolean;
  canCreateNew: boolean;
}

export const LISTING_AGENT_MATCH_FIELDS = {
  email: "email",
  phone: "phone",
  name: "name",
} as const;

/** The code a STRICT agent create (`rejectExisting`) answers with when the
 *  details belong to an agent already on file. Mirrors
 *  `ListingAgentAlreadyOnFileException.Code`. */
export const LISTING_AGENT_ALREADY_ON_FILE_CODE = "listingAgentAlreadyOnFile";

/** True when an error is the strict create's 409. Reads `details.code`, never
 *  the message, which is copy and will be reworded. */
export function isListingAgentAlreadyOnFile(e: unknown): boolean {
  if (!(e instanceof ApiError) || e.status !== 409) return false;
  const details = e.details as { code?: unknown } | null | undefined;
  return details?.code === LISTING_AGENT_ALREADY_ON_FILE_CODE;
}

export interface ListingDetailDto {
  id: string;
  rawUrl?: string | null;
  /** The dedupe comparison key, exposed so a specialist can see WHY two
   *  listings matched — an opaque "duplicate" warning is unarguable. */
  normalizedUrl?: string | null;
  businessName?: string | null;
  headline?: string | null;
  description?: string | null;
  industry?: string | null;
  marketplaceName?: string | null;
  listingReference?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  askingPrice?: number | null;
  cashFlow?: number | null;
  revenue?: number | null;
  inventory?: number | null;
  realEstateValue?: number | null;
  yearEstablished?: number | null;
  employeeCount?: number | null;
  listingAgentId?: string | null;
  listingAgent?: ListingAgentDto | null;
  rawPastedText?: string | null;
  capturedLinksJson?: string | null;
  aiModel?: string | null;
  notes?: string | null;
  submissionCount: number;
  createdAt: string;
  modifiedAt?: string | null;
  /** The listing's canonical label — see {@link ListingSummaryDto.displayName}. */
  displayName?: string;
  /** The listing broker's web contact form (D20). */
  contactFormUrl?: string | null;
}

export interface ListingListParams {
  page?: number;
  pageSize?: number;
  q?: string;
  industry?: string;
  state?: string;
  marketplaceName?: string;
  listingAgentId?: string;
  askingPriceMin?: number;
  askingPriceMax?: number;
  cashFlowMin?: number;
  hasSubmissions?: boolean;
  /** One rung of the engagement ladder, from clicking a segment of the stage
   *  bar. Dropped by the stats read, exactly as the sibling pages drop status —
   *  otherwise clicking a segment collapses the chart to that segment. */
  engagement?: string;
  /** Attention drill-throughs, each sharing its expression with its tile. */
  noAgentEmailOnly?: boolean;
  noAskingPriceOnly?: boolean;
  noBuyerPresentedWithinDays?: number;
}

/**
 * Tile counts for the Listings index.
 *
 * A listing has no status column — it is a SHARED catalog record two
 * specialists maintain together, so a stored status would be a second place its
 * engagement is written. `byEngagement` is derived server-side from the
 * listing's own submissions, keyed by the rungs in `LISTING_ENGAGEMENT`.
 */
export interface ListingStatsDto {
  total: number;
  /** Numerator of the velocity tile; `total` is its denominator, and both
   *  travel so a share can be read as the count it came from. */
  withBuyerPresented: number;
  /** ⚠️ Also the silent dedupe no-op: the agent get-or-create matches on EMAIL,
   *  so an agent recorded without one can never be found again and every later
   *  paste of the same person mints another row. Nothing fails and nothing is
   *  logged — the count is how it becomes visible. */
  noAgentEmail: number;
  noAskingPrice: number;
  noBuyerPresentedRecently: number;
  byEngagement: Record<string, number>;
  createdByMonth: MonthlyCountPoint[];
}

// ---------------------------------------------------------------------------
// Submissions
// ---------------------------------------------------------------------------

/**
 * Escalation PR-2 — where an escalated deal has got to, READ THROUGH the
 * pointer and never written back. Null on a submission that was not escalated.
 *
 * `stage` is the FUSED progression (`Submitted` → `DealStaging` → `Intake` →
 * `PreApproval` → `Underwriting` → `Closing` → `Closed`, plus the two
 * terminals) — one ladder spanning staging and the deal pipeline, not two
 * systems the reader has to join.
 */
export interface BuyerSubmissionEscalationProgressDto {
  dealSubmissionId: string;
  stage: string;
  /** The liaison returned it. Carried separately because `stage` folds
   *  "returned to you" into "your liaison is working it" — for the specialist
   *  those are the difference between having a job to do and not. */
  needsSpecialistAttention: boolean;
  dealId?: string | null;
  dealNumber?: string | null;
  /** Whether a liaison has imported it. A promoted shell sits at Intake and
   *  INVISIBLE until then, so a non-null `dealId` is not "it is live". */
  isInPipeline: boolean;
}

/** The gate's state, so the surface explains itself before anyone types. */
export interface BuyerSubmissionEscalationPreviewDto {
  canEscalate: boolean;
  /** Rendered VERBATIM — server-composed, so the pre-check and the refused
   *  POST cannot drift into saying different things. */
  blockedReason?: string | null;
  alreadyEscalated: boolean;
  escalatedDealSubmissionId?: string | null;
  listingAgentName?: string | null;
  brokerId?: string | null;
  brokerName?: string | null;
  brokerHasLiaison: boolean;
  hasSignedLoi: boolean;
  loiDocumentId?: string | null;
  /** Whether the submission's LISTING has a seller prequalification linked
   *  (Escalation PR-4). Listing-scoped, not submission-scoped. */
  sellerPrequalificationLinked?: boolean;
  sellerPrequalificationOutcome?: string | null;
  /** 🔴 A WARNING, never a blocker — it never touches `canEscalate`. A seller
   *  prequalification depends on a third party Phoenix cannot compel, so the
   *  hand-off proceeds either way. Server-composed; render it VERBATIM. */
  sellerPrequalificationWarning?: string | null;
}

export interface EscalateBuyerSubmissionPayload {
  sellerKind: "Person" | "Entity";
  sellerFirstName?: string | null;
  sellerLastName?: string | null;
  sellerEntityName?: string | null;
  loiDocumentId?: string | null;
  notes?: string | null;
}

export interface BuyerSubmissionEscalationResultDto {
  buyerSubmissionId: string;
  dealSubmissionId: string;
  dealId?: string | null;
  dealNumber?: string | null;
  brokerName: string;
  warnings: string[];
}

export interface BuyerSubmissionSummaryDto {
  id: string;
  buyerLeadId: string;
  buyerDisplayName: string;
  /** How a submissions LIST row names the buyer: "{Company} — {Person}" for a
   *  company buyer (`BuyerAcquirer.StaffDisplayName`), else `buyerDisplayName`.
   *  The "{buyer} → {listing}" pairing label and its chips keep
   *  `buyerDisplayName` (ruling Q5). Always sent by the server; optional so
   *  hand-built fixtures compile — read it as
   *  `buyerStaffDisplayName ?? buyerDisplayName`. */
  buyerStaffDisplayName?: string;
  listingId: string;
  listingBusinessName?: string | null;
  listingCity?: string | null;
  listingState?: string | null;
  listingAskingPrice?: number | null;
  status: BuyerSubmissionStatus;
  proposedOfferPrice?: number | null;
  nextFollowUpDate?: string | null;
  /** Server-computed, so the chip, the badge and the filter share one cutoff. */
  isFollowUpOverdue: boolean;
  conversationCount: number;
  lastConversationAtUtc?: string | null;
  canEdit: boolean;
  createdAt: string;
  /** Live deal progress for an escalated row; null for every other. */
  escalation?: BuyerSubmissionEscalationProgressDto | null;
  /**
   * Whether the NDA question is SETTLED — signed, OR explicitly recorded as not
   * required. False means only "nobody has got to it yet".
   *
   * Server-computed, so the buyer's summary strip, the submission's own surface
   * and any later filter share one definition. One bool cannot say WHICH way it
   * was settled, so a surface built on it must not word itself as though a
   * signature exists; the submission detail exposes both underlying fields.
   */
  ndaOnFile: boolean;
  /** The listing's canonical label, resolved SERVER-side: this DTO carries
   *  `listingBusinessName` and no other name column, so a consumer holding it
   *  cannot walk the rest of the ladder. Read through `listingNameOrFallback`. */
  listingDisplayName?: string;
  /** When an intro email about this listing FIRST went to the buyer — single or
   *  combined — or null. Shown beside a row a specialist may include in a
   *  combined intro; never enforced (ruling D6). */
  introEmailSentAtUtc?: string | null;
  /** The "Deal structure price" column: a COMPLETED structure's price, else
   *  null ("—"). Never `proposedOfferPrice`. */
  dealStructurePrice?: number | null;
  // --- Archive / delete (D1, D7, D8) — the server's own answers; render them,
  // never re-derive them. Optional: older payloads in tests omit them.
  isArchived?: boolean;
  archivedAtUtc?: string | null;
  /** Archive (or, when archived, restore) is allowed now. */
  canArchive?: boolean;
  /** Why archive is refused (escalated to a deal), for a caller who could otherwise archive. */
  archiveBlockedReason?: string | null;
  /** `Submissions.Delete` + ownership + never sent. */
  canDelete?: boolean;
  /** For a delete holder only: why Delete is greyed ("Already sent — archive instead"). */
  deleteBlockedReason?: string | null;
  /** Addendum B (B1): the listing's agent, for the Listing agent column. */
  listingAgent?: ListingAgentBriefDto | null;
}

export interface BuyerSubmissionDetailDto {
  id: string;
  buyerLeadId: string;
  buyerDisplayName: string;
  buyerAssignedSpecialistUserId?: string | null;
  listingId: string;
  listing?: ListingSummaryDto | null;
  status: BuyerSubmissionStatus;

  // Milestones (L19). Counting ALWAYS reads the stamp, never current status: a
  // submission that later dies must not revoke credit already earned.
  buyerApprovedAtUtc?: string | null;
  ndaSignedAtUtc?: string | null;
  ndaSentToBrokerAtUtc?: string | null;
  brokerRespondedAtUtc?: string | null;
  cimReceivedAtUtc?: string | null;
  financialsReceivedAtUtc?: string | null;
  meetingAtUtc?: string | null;
  loiAtUtc?: string | null;
  underContractAtUtc?: string | null;
  closedAtUtc?: string | null;

  ndaSignedByUserId?: string | null;
  ndaDocumentId?: string | null;
  ndaAuthorizationNote?: string | null;
  /** Why no NDA was required. Splits a blank `ndaSignedAtUtc`, which meant both
   *  "nobody has got to it yet" and "the broker never sent one". (PR 3a.) */
  ndaNotRequiredReason?: string | null;
  /** Which prequalification letter this package uses; null is the buyer's
   *  default. (PR 3b.) */
  prequalificationLetterOverrideSubmissionId?: string | null;

  askingPriceAtSubmission?: number | null;
  proposedOfferPrice?: number | null;
  downPaymentAmount?: number | null;
  downPaymentPercent?: number | null;
  sbaLoanAmount?: number | null;
  sellerNoteAmount?: number | null;
  sellerNoteTerms?: string | null;
  workingCapital?: number | null;
  contingencies?: string | null;
  targetCloseDate?: string | null;
  transitionPeriod?: string | null;

  nextFollowUpDate?: string | null;
  isFollowUpOverdue: boolean;
  notes?: string | null;

  canEdit: boolean;
  createdAt: string;
  modifiedAt?: string | null;

  // --- Escalation PR-2 ---------------------------------------------------
  /** The signed LOI, as a DMS Document id. */
  loiDocumentId?: string | null;
  /** The seller's name as typed at escalation — a display snapshot. */
  sellerNameAtEscalation?: string | null;
  /** Where the escalated deal has got to, read through the pointer. */
  escalation?: BuyerSubmissionEscalationProgressDto | null;

  /** Communications filed against this submission. Derived from the one
   *  communication log; PR E removed a duplicate store, not a useful count.
   *
   *  Optional on the client though the server always sends it: this record is
   *  built as an inline literal by a dozen test fixtures, and the convention
   *  here is that growing a DTO must not break them. */
  conversationCount?: number;

  // --- The offer wizard --------------------------------------------------
  // Optional for the same reason conversationCount is: a dozen fixtures build
  // this record as an inline literal.

  /** The wizard step to resume at, or absent if it was never started. */
  offerWizardStep?: number | null;

  /** When the offer was confirmed. Absent means unfinished, and an unfinished
   *  offer renders no Preliminary Offer Structure. */
  offerWizardCompletedAtUtc?: string | null;

  /** The specialist's one-line note on why this offer is strong (W6). */
  offerWhyThisOffer?: string | null;

  /** True when the target close was DEFAULTED rather than chosen — the document
   *  lists a defaulted date as open, never firm. */
  targetCloseDateIsDefaulted?: boolean;

  // --- Buyer-covered program fees ----------------------------------------

  /** How much of Phoenix's program fee the buyer carries: 0, 25, 50, 75 or 100.
   *
   *  🔴 Absent and 0 are DIFFERENT and both render nothing. Absent is a question
   *  nobody has asked; 0 is a buyer who was asked and declined, which is the
   *  state that stops them being asked again. */
  programFeeCoveragePercent?: number | null;

  /** The program fee rate in force when that answer was recorded, as a fraction.
   *
   *  🔴 The wizard's preview reads THIS, not a live figure — the client cannot
   *  see `Buyers:Offer:ProgramFeeRate` and must not re-quote a buyer at a rate
   *  that moved after they were given one. Null until a coverage answer exists,
   *  which is when the wizard falls back to its mirrored default. */
  programFeeRateAtQuote?: number | null;

  /** The fee the buyer was quoted as capitalizing. Held so the quote stays
   *  reconstructable; no surface renders it. */
  programFeeBuyerAmountAtQuote?: number | null;

  // --- The Potential Deal Structure (document 3) --------------------------
  // Optional for the same reason conversationCount is. 🔴 Completion keys ONLY
  // on `dealStructureCompletedAtUtc` — never on `offerWizardCompletedAtUtc`,
  // which is backfilled on every historical row.

  /** BUILD THE DEAL STRUCTURE's resume step; absent when never started. */
  dealStructureStep?: number | null;
  /** When the structure was saved complete (forward-only). */
  dealStructureCompletedAtUtc?: string | null;
  /** The structure's price — present only once it is complete. */
  dealStructurePrice?: number | null;

  // --- Archive / delete — see the summary DTO.
  isArchived?: boolean;
  archivedAtUtc?: string | null;
  /** D5's banner: "Archived {date} by {name}". */
  archivedByName?: string | null;
  canArchive?: boolean;
  archiveBlockedReason?: string | null;
  canDelete?: boolean;
  deleteBlockedReason?: string | null;
}

export interface BuyerSubmissionListParams {
  page?: number;
  pageSize?: number;
  q?: string;
  buyerLeadId?: string;
  listingId?: string;
  status?: BuyerSubmissionStatus[];
  /** Excludes the terminal statuses without enumerating the live ones. */
  liveOnly?: boolean;
  assignedSpecialistUserId?: string;
  mineOnly?: boolean;
  overdueFollowUpOnly?: boolean;
  /** Attention drill-throughs. Each shares its server-side expression with the
   *  tile that counted it. */
  awaitingBrokerResponseDays?: number;
  packageGeneratedNotSentOnly?: boolean;
  ndaOutstandingOnly?: boolean;
  noActivityWithinDays?: number;
  /** D4 "Show archived": include archived rows. Off hides them everywhere;
   *  the Reports tab sends it so its figures don't change (R6). */
  includeArchived?: boolean;
}

// ---------------------------------------------------------------------------
// Assignment queue
// ---------------------------------------------------------------------------

/** Which buyer-module role a roster row is listed under. F2 wins for somebody
 *  holding both, so a person appears in exactly one group. */
export type BuyerRoleCode = "F1" | "F2";

export interface BuyerSpecialistRosterRowDto {
  specialistUserId: string;
  displayName?: string | null;
  buyerRoleCode: BuyerRoleCode;
  activeBuyers: number;
  qualifiedBuyers: number;
  activelySubmittingBuyers: number;
  liveSubmissions: number;
  lastAssignedAtUtc?: string | null;
}

/**
 * How WIDE the caller's dispatch scope is. Mirrors `BuyerAssignmentScopeKinds`.
 *
 * 🔴 The client BRANCHES on this and never re-derives scope from the counts: a
 * caller on no team and a caller whose team is genuinely clear both get zero
 * rows and an empty roster, and "The queue is clear" is a false statement to
 * the first of them — which is exactly what the page used to say.
 */
export type BuyerAssignmentScopeKind = "AllTeams" | "OneTeam" | "NoScope";

export interface BuyerAssignmentQueueDto {
  totalUnassigned: number;
  staleCount: number;
  staleAfterDays: number;
  buyers: BuyerLeadSummaryDto[];
  /** Everyone who may be given a buyer, each with the book they hold — NOT only
   *  the people already holding one. It was `specialistLoad` and was built by
   *  grouping assigned buyers by owner, so an eligible person with an empty book
   *  was absent and the picker could never place the first buyer. */
  specialistRoster: BuyerSpecialistRosterRowDto[];
  /** Server-resolved. `"NoScope"` means the caller dispatches nothing — render
   *  the "you are not on a buyer team" state, not an empty queue. */
  scopeKind: BuyerAssignmentScopeKind;
}

/** One person the caller may hand a buyer to. */
export interface BuyerSpecialistCandidateDto {
  specialistUserId: string;
  displayName?: string | null;
  buyerRoleCode: BuyerRoleCode;
  buyerTeamId?: string | null;
  buyerTeamName?: string | null;
  activeBuyers: number;
}

/**
 * The people the caller may hand a buyer to, resolved SERVER-SIDE for one
 * buyer — or for a not-yet-created one when no id is passed. ONE rule
 * (`BuyerAssignmentService.ListCandidatesAsync`): a `Buyers.ManageAll` holder
 * sees the specialists on the teams they dispatch; anybody else sees their OWN
 * team's specialists, and only for a buyer they currently own. The create
 * forms and the bulk bar read this, and the row quick-pick reads the same
 * people through `BuyerHandOffRosterDto`, rather than each deciding who is
 * offerable.
 */
export interface BuyerSpecialistCandidatesDto {
  scopeKind: BuyerAssignmentScopeKind;
  candidates: BuyerSpecialistCandidateDto[];
  /** Whether the caller may return THIS buyer to the queue. Always false when
   *  no buyer was named or the buyer has no owner. */
  canUnassign: boolean;
}

/**
 * Who the caller may hand an EXISTING buyer to — the `candidates` answer for
 * any buyer they may reassign, which depends only on the caller
 * (`BuyerAssignmentService.ListHandOffRosterAsync`). A page of rows reads this
 * once; each row carries its own `canReassign` / `canUnassign`.
 */
export interface BuyerHandOffRosterDto {
  scopeKind: BuyerAssignmentScopeKind;
  candidates: BuyerSpecialistCandidateDto[];
}

export interface BuyerAssignmentQueueParams {
  page?: number;
  pageSize?: number;
  q?: string;
  source?: BuyerLeadSource[];
  staleOnly?: boolean;
  /** Narrow a merged all-teams queue to one team (D6). Meaningful only for a
   *  `Buyers.ManageAllTeams` holder — for anybody else the server has already
   *  scoped the queue to their own team and this is ignored, never widened. */
  buyerTeamId?: string;
}

export interface AssignBuyerPayload {
  specialistUserId: string;
  reason?: string;
}

export interface BulkAssignBuyersPayload {
  specialistUserId: string;
  buyerLeadIds: string[];
  /** Required. Without it a retried bulk assign double-credits an append-only ledger. */
  idempotencyKey: string;
  reason?: string;
}

export interface BulkAssignItemResultDto {
  buyerLeadId: string;
  outcome: "assigned" | "skipped" | "failed";
  detail?: string | null;
}

export interface BulkAssignResultDto {
  requested: number;
  assigned: number;
  skipped: number;
  failed: number;
  items: BulkAssignItemResultDto[];
}

// ---------------------------------------------------------------------------
// Allocation policy + entitlement (PR 2b)
// ---------------------------------------------------------------------------

/**
 * One layer's AUTHORED values. Every member is nullable and `null` means
 * INHERIT — from the global row on an override, and from the shipped defaults on
 * the global row. 🔴 It never means zero, so a cleared field must be sent as
 * `null` and never coerced to `0` on the way out.
 */
export interface BuyerAllocationValuesDto {
  startingAllocation?: number | null;
  qualifyingThreshold?: number | null;
  /** Non-positive is "explicitly uncapped", which is a DIFFERENT state from the
   *  null that inherits whatever the layer beneath says. */
  maxActiveBuyers?: number | null;
  notes?: string | null;
  /** Days of silence before a live buyer reads as stale (D10). Authored on the
   *  GLOBAL row and a TEAM tier only — the server refuses it on a specialist
   *  override — and `null` inherits, exactly like the three above. */
  staleContactDays?: number | null;
}

/** What a layer resolves to once everything beneath it is applied. Nothing here
 *  is ambiguous except `maxActiveBuyers`, which is null exactly when uncapped. */
export interface ResolvedBuyerAllocation {
  startingAllocation: number;
  qualifyingThreshold: number;
  maxActiveBuyers?: number | null;
}

export interface BuyerAllocationOverrideDto {
  specialistUserId: string;
  displayName?: string | null;
  authored: BuyerAllocationValuesDto;
  effective: ResolvedBuyerAllocation;
  modifiedAt?: string | null;
  /** The EFFECTIVE stale threshold this specialist's team runs on (team tier ??
   *  global ?? platform default). Read-only here: it is never authored on an
   *  override. Optional so existing fixtures keep compiling. */
  staleContactDays?: number;
}

/** One team's tier — the layer between the global row and a specialist's
 *  override. `authored` is what somebody typed; `effective` is what it resolves
 *  to over the global row. */
export interface BuyerAllocationTeamTierDto {
  buyerTeamId: string;
  buyerTeamName: string;
  authored: BuyerAllocationValuesDto;
  effective: ResolvedBuyerAllocation;
  modifiedAt?: string | null;
  /** The EFFECTIVE stale threshold for this team (its own ?? global ??
   *  platform default). Optional so existing fixtures keep compiling. */
  staleContactDays?: number;
}

export interface BuyerAllocationSettingsDto {
  global: BuyerAllocationValuesDto;
  effectiveDefaults: ResolvedBuyerAllocation;
  overrides: BuyerAllocationOverrideDto[];
  /** Per-team tiers, narrowed to the teams the caller dispatches. Optional on
   *  the type so existing fixtures keep compiling; the server always sends it. */
  teamTiers?: BuyerAllocationTeamTierDto[];
  /** Whether the caller may write the GLOBAL row — all-teams holders only. A
   *  one-team manager edits their team's tier instead. */
  canEditGlobal?: boolean;
}

/**
 * A settings write. An ALL-NULL body deletes the row — that is how "reset to
 * inherited" is spelled, and it is why every field on the editor has to be able
 * to express "cleared" distinctly from "zero".
 */
export interface SaveBuyerAllocationPayload {
  startingAllocation?: number | null;
  qualifyingThreshold?: number | null;
  maxActiveBuyers?: number | null;
  notes?: string | null;
  /** 1..365, or null to inherit. 🔴 Global and team tiers ONLY — a non-null
   *  value on a specialist override is a 400. Leave it undefined there. */
  staleContactDays?: number | null;
}

export interface BuyerEntitlementDto {
  specialistUserId: string;
  displayName?: string | null;
  /** Submissions with a package-sent stamp — counted from the stamp, not the status. */
  qualifyingCount: number;
  entitlement: number;
  currentlyHeld: number;
  /** entitlement − currentlyHeld. Negative is legitimate after a manual assign. */
  gap: number;
  /** Qualifying submissions toward the next earned buyer — the "7" in "7 of 10". */
  towardNext: number;
  qualifyingThreshold: number;
  maxActiveBuyers?: number | null;
  isEligible: boolean;
}

// ---------------------------------------------------------------------------
// Auto-Distribute (PR 2b)
// ---------------------------------------------------------------------------

export interface BuyerAutoDistributeItemDto {
  buyerLeadId: string;
  buyerName?: string | null;
  specialistUserId?: string | null;
  specialistDisplayName?: string | null;
  assigned: boolean;
  /** Why this buyer stayed in the queue. Null on an assigned item. */
  reason?: string | null;
}

/**
 * 🔴 On a PREVIEW run the server reports `assigned: 0` and counts the whole
 * queue against `skipped` minus the plan — the plan itself lives in `items`. So
 * a preview's headline numbers must be derived from `items`, and only an APPLIED
 * run may be read off `assigned`/`skipped`.
 */
export interface BuyerAutoDistributeResultDto {
  considered: number;
  assigned: number;
  skipped: number;
  /** The standings the plan was decided from, before the run moved anything. */
  specialists: BuyerEntitlementDto[];
  items: BuyerAutoDistributeItemDto[];
}

export interface BuyerAutoDistributePayload {
  /** Compute and report without assigning anything. */
  preview?: boolean;
  /** Null or non-positive means no bound. */
  maxAssignments?: number | null;
  reason?: string | null;
}

// ---------------------------------------------------------------------------
// Submission package (PR 2c)
// ---------------------------------------------------------------------------

/** One document's editable narrative blocks, keyed as the server names them. */
export type BuyerPackageNarrativeMap = Record<string, string>;

/**
 * The documents, as the server names them.
 *
 * 🔴 The enum MEMBER NAME, not the number. The wire carries the name (the
 * platform's global JSON string-enum converter), and a client keying on an
 * ordinal would silently re-point every card the first time a member was
 * inserted.
 *
 * <p>🔴 <b>NOTHING IN THE SPA IS EXHAUSTIVE OVER THIS UNION.</b> There is no
 * `Record<BuyerSubmissionDocumentKind, …>` anywhere — every consumer either
 * iterates the server's own `documents` array or treats a kind as an opaque
 * string — so TypeScript will NOT flag a missing arm when a member is added.
 * A new kind therefore lands silently and is only ever wrong at runtime. The
 * places that DO look at a kind are enumerated on `SubmissionPackagePanel`; add
 * one here and read that list.</p>
 *
 * <p>⚠️ This comment used to open "The five documents". A count typed into
 * prose is a fact about the enum that nothing checks, and it was already wrong
 * by two when the buyer's cover letter and this brochure arrived. The count
 * lives where it is derived — see the header badge, which counts the array.</p>
 */
export type BuyerSubmissionDocumentKind =
  | "CoverLetter"
  | "BuyerProfile"
  | "OfferStructure"
  | "NextStepsAndAboutPhoenix"
  | "PrequalificationLetter"
  | "BuyerCoverLetter"
  | "SellerBrochure";

/**
 * One document's card.
 *
 * Every gate is resolved server-side, so a button's enabled state and the
 * server's refusal cannot drift into disagreeing. Render the reason you are
 * given; never invent one.
 */
export interface BuyerSubmissionDocumentDto {
  kind: BuyerSubmissionDocumentKind;
  title: string;
  subtitle: string;
  /**
   * False for a document Phoenix does not COMPOSE for this package — the
   * prequalification letter, which is pulled from the funnel, and the seller
   * program brochure, which is a single globally-owned PDF resolved live.
   * Neither card offers Generate or Edit.
   *
   * <p>🔴 <b>It is no longer a complete discriminator.</b> It used to answer
   * "which shape is this card" on its own, because there was exactly one
   * non-authored kind. There are two now and they differ — the letter is per
   * buyer and chosen, the brochure is per organisation and fixed — so the panel
   * tells them apart by KIND. Do not restore "never branch on the kind string"
   * as an absolute; see the seam list on `SubmissionPackagePanel`.</p>
   */
  isAuthored: boolean;
  /** Null until this document has been rendered once. */
  generatedAtUtc: string | null;
  contentHash: string | null;
  /** The filed DMS document. Null does NOT mean unavailable — the download
   *  re-renders from the persisted narrative. */
  documentId: string | null;
  /** Narrowed to the keys THIS document owns, so an editor never shows a
   *  sibling's prose. */
  narrative: BuyerPackageNarrativeMap | null;
  /**
   * 🔴 True when this text was CARRIED OVER from the retired single-package
   * narrative rather than composed for this document.
   *
   * The card must say so. A carried document holds one section of the old
   * ten-section package while a generated one is several times longer, and the
   * two are otherwise indistinguishable on screen — which makes the specialist
   * who cannot tell them apart the one who sends the thin one.
   */
  carriedOver: boolean;
  /** True when a sibling has been generated or edited since this one was
   *  written. Only ever true for the cover letter, which is the only document
   *  that describes the others. Surfaced, never enforced. */
  describesStaleSiblings: boolean;
  /**
   * The NON-AUTHORED state: `present` | `absent` | `stale`.
   *
   * <p>🔴 <b>Shared by the prequalification letter AND the seller brochure,
   * and the name is deliberately NOT `sourceState` — owner ruling.</b> Renaming
   * a live wire field to widen its meaning buys a better word and costs a
   * co-ordinated two-tier change on a field the panel branches on; the meaning
   * is recorded here instead. Read it as "is the pulled thing there", never as
   * "is a letter there".</p>
   */
  letterState: string | null;
  /** The sentence for that state — composed server-side, so the panel never
   *  assembles a reason from parts. Also shared by both non-authored kinds:
   *  the server says which thing is missing, the card never guesses. */
  letterMessage: string | null;
  /**
   * The active brochure's own file, for the card that shows it.
   *
   * <p>🔴 <b>Populated for `SellerBrochure` and NULL for every other kind</b>
   * — including the prequalification letter, which is rendered per buyer and has
   * no single uploaded file behind it. So a card must render this metadata line
   * from the fields themselves and never from "this document is non-authored".
   * All three are independently nullable: a brochure nobody has uploaded carries
   * none of them, and a page count is the one a server may legitimately fail to
   * read off a PDF while the file itself is fine.</p>
   */
  sourceFileName: string | null;
  sourceSizeBytes: number | null;
  sourcePageCount: number | null;
  /**
   * Why this document cannot be generated yet, or null when nothing blocks it.
   *
   * Today only document 3 (the Potential Deal Structure) can carry one: its
   * deal structure has not been saved complete. It is a SKIP rather than a
   * failure: `generateAll` writes the others and this card says what to do
   * about this one. Composed server-side by the SAME expression the generator
   * consults, so a card can never offer Generate on a document the generator
   * will silently pass over.
   */
  blockedReason: string | null;
  /** Q1 — a generated document 3 in an older format (anything made before the
   *  Potential Deal Structure). The card flags it, an edit is refused, and a
   *  send carrying it asks for confirmation. Optional so an older server reads
   *  false. */
  olderFormat?: boolean;
  /** The older-format flag's sentence, composed server-side. Render it
   *  verbatim when present. */
  olderFormatNote?: string | null;
  /** Q35 — something the specialist entered was left off a document because of
   *  its wording. Null when nothing was left out. */
  omissionNotice?: string | null;
  /** Whether `getDocumentDocx` serves an editable Word copy of this document.
   *  A staff download only — never listed in a send. Optional so an older
   *  server reads false. */
  wordAvailable?: boolean;
}

/** The whole package — the panel's single read. One card per document kind. */
export interface BuyerSubmissionPackageDto {
  submissionId: string;
  /** Every kind, in document order. A document that has never been generated is
   *  present with a null `generatedAtUtc` rather than absent, so the panel
   *  renders a card per kind without inventing the missing ones. */
  documents: BuyerSubmissionDocumentDto[];
  status: BuyerSubmissionStatus;
  /**
   * 🔴 Never OR this with `sentToBuyerAtUtc` into "has it been sent". They are
   * two independent sends in either order, and only this one drives the header
   * badge, the status and what the specialist has earned.
   */
  sentAtUtc: string | null;
  sentToBuyerAtUtc: string | null;
  /** The single PDF generated under the previous model, when this submission
   *  has one — the frozen record of what actually went out. Its documents are
   *  NOT this PDF carved up; nothing splits it. */
  legacyPackageDocumentId: string | null;
  legacyPackageGeneratedAtUtc: string | null;
  buyerEmail: string | null;
  canSendToBuyer: boolean;
  sendToBuyerBlockedReason: string | null;
  /** True when a broker send would go out with no Phoenix prequalification
   *  letter. Does NOT block — it is what makes the send ask. */
  sendNeedsNoLetterConfirmation: boolean;
  /** The sentence the confirmation renders. Composed server-side so it names
   *  the buyer. */
  noLetterConfirmationMessage: string | null;
  /** D8 — "Buyer handles broker communications", the live switch. */
  buyerHandlesBrokerCommunications: boolean;
  /** Why the switch cannot be turned on (no phone or email on the buyer
   *  record), or null when it can. */
  buyerHandlesBrokerCommunicationsBlockedReason: string | null;
  /** The switch is on but the record lost its phone and email — both sends
   *  refuse with `BUYER_PACKAGE_DIRECT_CONTACT_MISSING` until fixed. */
  directContactMissing: boolean;
  /** Q18 — the buyer's identity changed since a document was generated. A
   *  NON-BLOCKING "regenerate" notice; never gates a send. */
  buyerIdentityChangedSinceGeneration: boolean;
  /** The cover letter's Referral Disclosure for this submission (D5/D6/D8).
   *  Null only when the buyer row could not be read. */
  referralDisclosure?: BuyerReferralDisclosureState | null;
}

/** A broker as the Referral Disclosure surfaces show it — the raw name fields
 *  the preview cleans (`lib/referralDisclosure.ts`), the stage, and the "by …"
 *  phrase the letter prints (`printedName`, null when nothing is printable). */
export interface ReferralBroker {
  id: string;
  companyName: string;
  dba: string | null;
  name: string | null;
  email: string | null;
  /** The `BrokerStatus` name: Prospect, Qualified, Onboarding, Certified, … */
  status: string;
  /** In the archive set or deleted: stays on a saved setting, can't be newly
   *  picked (D8). */
  isArchived: boolean;
  printedName: string | null;
}

/** Where a submission's effective disclosure came from. */
export type ReferralDisclosureSource = "BuyerDefault" | "Submission" | "SentBeforeSetting";

export interface BuyerReferralDisclosureState {
  enabled: boolean;
  source: ReferralDisclosureSource;
  /** The effective broker; null when off. */
  broker: ReferralBroker | null;
  feePercent: number | null;
  /** The body exactly as the cover letter prints it; null when off. */
  disclosureText: string | null;
  /** The buyer name the disclosure uses (the company for a company buyer). */
  buyerNameAsPrinted: string;
  buyerDefaultBroker: ReferralBroker | null;
  buyerDefaultFeePercent: number | null;
  /** D9 — the broker-owned prequalification site's owner, while neither a
   *  buyer default nor an override exists. Pre-fills the picker only. */
  suggestedBroker: ReferralBroker | null;
  /** D6 — the listing's listing broker as a Broker id (id links only). */
  listingBrokerId: string | null;
  /** The latest BROKER package send, and whether it carried the disclosure. */
  lastSentAtUtc: string | null;
  lastSentIncluded: boolean;
}

export interface SetReferralDisclosurePayload {
  enabled: boolean;
  brokerId?: string | null;
  feePercent?: number | null;
}

/** The buyer's "Referred by" (D4) and the D9 suggestion. */
export interface BuyerReferredByDto {
  broker: ReferralBroker | null;
  feePercent: number | null;
  suggestedBroker: ReferralBroker | null;
  canEdit: boolean;
}

/** `BuyerPackageDirectContactMissingException.Code` — branch on this, never on
 *  the message. */
export const BUYER_PACKAGE_DIRECT_CONTACT_MISSING = "buyerPackageDirectContactMissing";

export interface UpdateBuyerSubmissionDocumentPayload {
  /** Partial by design — omitted keys keep their current text. */
  narrative: Record<string, string | null>;
}

/** One file a send will attach — name and size, never bytes. */
export interface BuyerPackageAttachmentDto {
  kind: BuyerSubmissionDocumentKind;
  title: string;
  fileName: string;
  sizeBytes: number;
  /** Prose carried forward from the retired single-file package. The send modal
   *  is where this matters — a carried-over document is a fraction of the length
   *  of a generated one and looks identical on screen. */
  carriedOver: boolean;
}

/**
 * Exactly what a send will attach, and what it will not.
 *
 * 🔴 Resolved ONCE, server-side, and shared by this preview and the send
 * itself. A preview that listed one set of files while the send attached
 * another would defeat the whole point of showing it.
 */
export interface BuyerPackageAttachmentManifestDto {
  attachments: BuyerPackageAttachmentDto[];
  /** Documents not yet generated, by title. Named so a specialist can see them
   *  and decide — this does not block, and there is no minimum. */
  missingTitles: string[];
  prequalificationLetterMissing: boolean;
  attachmentLimitBytes: number;
  /** Computed server-side over the whole set. Sums, because there are several
   *  files where there was one. */
  totalSizeBytes: number;
  /** Whether a Phoenix send would refuse. Computed from the limit beside it, so
   *  this warning and the server's refusal cannot disagree about the line. */
  overAttachmentLimit: boolean;
  /**
   * Which kinds start ticked, decided by the SERVER from the audience.
   *
   * 🔴 The resolved set is the same for both sends; only the initial tick
   * differs. A listing broker has no use for the buyer's own copy of the
   * package, and the buyer receives document 1 on purpose — it is the letter
   * describing them, which is exactly what they need to be able to correct.
   *
   * Optional so a fixture that predates it still type-checks; absent means
   * "everything ticked", which is what the surface did before.
   */
  defaultSelectedKinds?: BuyerSubmissionDocumentKind[];
}

export interface BuyerPackageDownloadDto {
  attachments: BuyerPackageAttachmentManifestDto;
  subject: string;
  body: string;
  /** The sentence the specialist must accept before a broker send with no
   *  prequalification letter dispatches. Null when a letter is enclosed. */
  noLetterConfirmationMessage: string | null;
  /** 🔴 The listing agent, RESOLVED. This preview used to name nobody and say
   *  "the address on file", so a listing with a missing or malformed agent email
   *  previewed cleanly and failed only at submit. */
  toName: string | null;
  toEmail: string | null;
  /** Why this send cannot go, when it cannot. Server-composed — render it, never
   *  compose a second one here. */
  recipientBlockedReason: string | null;
  /** Phoenix's regulated role statement, SEPARATE from the body so it can be
   *  shown without being editable. The transport decorator is the only thing
   *  that puts it on the wire. */
  roleStatement: string;
}

export interface SendBuyerPackagePayload {
  toEmail?: string | null;
  subject?: string | null;
  body?: string | null;
  /**
   * 🔴 The specialist's explicit acknowledgement that this package carries no
   * Phoenix prequalification letter.
   *
   * A second ACTION, never a pre-ticked box. Send it true only after the person
   * has confirmed in a dialog that names what is missing.
   */
  confirmNoPrequalificationLetter?: boolean;
  /** The document kinds to attach. Omitted or empty means every generated
   *  document — what every pre-picker client sent. Applied server-side to the
   *  set the preview already listed, so the two cannot disagree. */
  includeKinds?: BuyerSubmissionDocumentKind[] | null;
  /** Q1 — the specialist confirmed sending a document 3 still in the older
   *  format. Send true only after a dialog that says so. */
  confirmOlderFormatDocument?: boolean;
}

/** The machine-readable code a no-letter refusal carries in `details`.
 *
 *  🔴 Branch on THIS, never on the message. The message is copy and will be
 *  reworded; a string match would stop matching the moment it was — and what it
 *  would silently stop doing is opening the confirmation, which turns a
 *  decision into a dead end. */
export const NO_PREQUALIFICATION_LETTER_CODE = "buyerPackageNoPrequalificationLetter";

/** Q1 — the send carries a document 3 still in the older format. Branch on
 *  this code, never on the message. Both sends can return it. */
export const OLDER_FORMAT_DOCUMENT_CODE = "buyerPackageOlderFormatDocument";

export interface SendBuyerPackageResultDto {
  toEmail: string;
  subject: string;
  sentAtUtc: string;
  /** Reported SEPARATELY from the send: the email is irreversible and the log
   *  entry is not, so a bookkeeping failure must never read as a send failure. */
  communicationLogged: boolean;
  /** How many files went out. Reported because a send no longer attaches
   *  exactly one thing. */
  attachmentCount: number;
}

/**
 * The buyer-facing cover email for the SAME documents the listing broker gets.
 *
 * Carries the resolved recipient as well as the copy, so the confirmation states
 * who it is going to. There is no address field in this flow — the email has one
 * correct recipient, and a buyer with no address is refused before the modal
 * opens.
 *
 * 🔴 No confirmation field, and that is the ruling rather than an omission. The
 * no-letter gate exists to stop Phoenix presenting an unqualified buyer to a
 * COUNTERPARTY; showing a buyer their own package presents them to nobody.
 */
export interface BuyerPackageBuyerDraftDto {
  attachments: BuyerPackageAttachmentManifestDto;
  subject: string;
  body: string;
  toEmail: string | null;
  toName: string;
  /** See `BuyerPackageDownloadDto.roleStatement`. Shown, never edited. */
  roleStatement: string;
}

export interface SendBuyerPackageToBuyerPayload {
  subject?: string | null;
  body?: string | null;
  /** The document kinds to attach. Omitted or empty means every generated
   *  document — what every pre-picker client sent. Applied server-side to the
   *  set the preview already listed, so the two cannot disagree. */
  includeKinds?: BuyerSubmissionDocumentKind[] | null;
  /** See `SendBuyerPackagePayload.confirmOlderFormatDocument`. */
  confirmOlderFormatDocument?: boolean;
}

// ---------------------------------------------------------------------------
// Listing intro email (WS-10) — a SIBLING of the package above, never a
// variation of it.
//
// 🔴 Different recipient, different moment, different stamp. The package goes
// to the LISTING BROKER once a buyer has said yes; this goes to the BUYER, to
// find out whether there is anything to take to a broker at all. Sending it
// writes `IntroEmailSentAtUtc` and never `PackageSentAtUtc`, which the
// specialist entitlement engine counts.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Combined intro email — ONE email to ONE buyer introducing several of their
// listings (2-50). Not the single intro above (one listing, sent from that
// listing's own tab) and not the broker intro (to a listing broker). Rules:
// `lib/buyerCombinedIntroRules.ts`, mirrored from BuyerCombinedIntroRules.cs.
// ---------------------------------------------------------------------------

export type BuyerCombinedIntroSentVia = "Phoenix" | "Copy";

export interface BuyerCombinedIntroListingDto {
  submissionId: string;
  /** 1-based, in the order selected; the body and the listing-page section use
   *  the same numbers. */
  number: number;
  listingName: string;
  status: BuyerSubmissionStatus;
  /** The status label when this listing can no longer go in a combined intro,
   *  or null. Any blocked listing disables Send and Copy. */
  blockedReason: string | null;
  introEmailSentAtUtc: string | null;
  /** The qualifying https page, or null — never an http/relative/empty value. */
  listingPageUrl: string | null;
  usedFixedWording: boolean;
}

export interface BuyerCombinedIntroEmailDto {
  id: string;
  buyerLeadId: string;
  buyerName: string;
  buyerEmail: string | null;
  mode: CombinedIntroMode;
  includeListingLinks: boolean;
  subject: string;
  /** The editable message only — the listing-page section, signature and role
   *  statement are added after it. */
  body: string;
  /** The locked section exactly as it will be sent, or null. */
  listingPagesSection: string | null;
  listings: BuyerCombinedIntroListingDto[];
  aiModel: string | null;
  draftedAtUtc: string;
  savedAtUtc: string | null;
  sentAtUtc: string | null;
  sentVia: BuyerCombinedIntroSentVia | null;
  canSend: boolean;
  blockedReason: string | null;
}

export interface DraftBuyerCombinedIntroPayload {
  submissionIds: string[];
  includeListingLinks?: boolean;
  /** Required when the buyer already has an unsent draft. */
  replaceDraftId?: string | null;
}

export interface UpdateBuyerCombinedIntroPayload {
  body: string;
  includeListingLinks: boolean;
}

export interface SendBuyerCombinedIntroPayload {
  body: string;
  includeListingLinks: boolean;
  /** The listings on screen; refused when they no longer match the draft. */
  expectedSubmissionIds: string[];
}

export interface SendBuyerCombinedIntroResultDto {
  email: BuyerCombinedIntroEmailDto;
  toEmail: string;
  sentAtUtc: string;
  /** False when the email WENT but recording it on the listings failed. */
  recorded: boolean;
  recordingFailedMessage: string | null;
  communicationLogged: boolean;
}

export interface BuyerCombinedIntroCopyDto {
  subject: string;
  body: string;
  toEmail: string | null;
  email: BuyerCombinedIntroEmailDto;
}

export interface BuyerIntroEmailStateDto {
  submissionId: string;
  buyerName: string;
  /** Where a Phoenix send would go. Null when the buyer has no address on
   *  file, which is one of the two reasons `canSend` is false. */
  buyerEmail: string | null;
  listingName: string;
  /** Composed server-side and never stored — there is nothing to edit and so
   *  nothing to keep. Shown so the whole message is on screen before it goes. */
  subject: string;
  bodyDraft: string | null;
  generatedAtUtc: string | null;
  sentAtUtc: string | null;
  /** What actually went out, if anything has — kept beside the draft so a
   *  specialist can see whether the draft still says what the buyer was told. */
  sentBody: string | null;
  /** The model that wrote the draft, or null when the deterministic fallback
   *  did. Null is meaningful, not missing. */
  aiModel: string | null;
  /** Resolved server-side, so the button's enabled state and the endpoint's
   *  refusal come from one rule. Never compose a second reason client-side. */
  canSend: boolean;
  blockedReason: string | null;

  /** The listing's page as it may be printed in an email — an absolute https
   *  URL, exactly as stored — or null when it has none, which is when the
   *  composer says "No https listing page on file." instead of offering the
   *  toggle. Optional on the type so fixtures written before it still compile;
   *  the server always sends it. */
  listingPageUrl?: string | null;
  /** The exact line "Include the listing page" appends after the message
   *  (copy C7). Composed server-side so the preview shows the text that will
   *  go. Null exactly when `listingPageUrl` is. */
  listingPageLine?: string | null;
  /** The listing-page line the LATEST send carried, already part of
   *  `sentBody`. Held apart so "has the draft changed since it went out?"
   *  compares the message the consultant wrote, not the line the system
   *  added. */
  sentListingPagesSection?: string | null;
  /** Set when the latest send went out as part of a combined intro — one
   *  email introducing several listings. `sentBody` is then that whole email,
   *  which is not comparable with this listing's own draft. */
  latestSendCombinedIntroEmailId?: string | null;
  /** How many OTHER listings that combined email introduced. */
  latestSendCombinedOtherCount?: number | null;
  /** When that combined email went out. Null unless the latest send was
   *  combined. */
  latestSendCombinedAtUtc?: string | null;
  /** This listing's number in that combined email (its position in the email's
   *  ordered ids). Null unless the latest send was combined. The card shows the
   *  listing's own entry only when the sent body's numbers still run 1…N (J4). */
  latestSendCombinedPosition?: number | null;
  /** The buyer's first name, for "Sent as part of one email to {first name}…".
   *  Null when there is none; the card then uses `buyerName`. */
  buyerFirstName?: string | null;
}

/** The single intro's send-time choice. Never stored on the draft. */
export interface SendBuyerIntroEmailPayload {
  /** "Include the listing page" — append the listing's https page as one line
   *  after the message. Off by default. */
  includeListingLink: boolean;
}

export interface UpdateBuyerIntroEmailPayload {
  /** The whole body, not a patch — the composer is a textarea, and sending a
   *  diff of prose would be a protocol nobody needs. */
  body: string;
}

export interface SendBuyerIntroEmailResultDto {
  toEmail: string;
  subject: string;
  sentAtUtc: string;
  /** Reported SEPARATELY from the send: the email is irreversible and the log
   *  entry is not, so a bookkeeping failure must never read as a send failure. */
  communicationLogged: boolean;
  /** The refreshed state, so the surface need not re-read to show the send it
   *  just performed. */
  state: BuyerIntroEmailStateDto;
}

export interface BuyerIntroEmailDownloadDto {
  subject: string;
  /** No signature block, deliberately — this is pasted into the specialist's
   *  OWN client, which appends theirs. */
  body: string;
  /** Null when the buyer has no address on file, which does NOT block this
   *  path: a specialist may well have an address we do not. */
  toEmail: string | null;
  /** Requesting the copy STAMPS the send, so the caller is handed the state
   *  that stamp produced. */
  state: BuyerIntroEmailStateDto;
}

// ---------------------------------------------------------------------------
// Broker intro email — the step BEFORE the package
//
// 🔴 The third outward artifact, and the only one that PRECEDES the thing it is
// about. The package goes to the listing broker once a buyer has said yes; the
// intro email goes to the buyer; this goes to the listing broker to ask whether
// they would like the package at all. Sending it writes
// `BrokerIntroEmailSentAtUtc` and NOTHING else — not `PackageSentAtUtc`, which
// the entitlement engine counts, and not the buyer's contact clocks, because a
// broker email must never make the buyer look contacted.
// ---------------------------------------------------------------------------

export interface BrokerIntroEmailStateDto {
  submissionId: string;
  buyerName: string;
  listingName: string;
  /** The listing broker, named. Null when the listing carries no agent — which
   *  is one of the two reasons a Phoenix send is unavailable, and the only
   *  reason recording a copy-path send is. */
  agentName: string | null;
  /** Where a Phoenix send would go. Null when the agent has no address on
   *  file. */
  agentEmail: string | null;
  /** Composed server-side and never stored — nothing to edit, so nothing to
   *  keep. Shown so the whole message is on screen before it goes. */
  subject: string;
  bodyDraft: string | null;
  generatedAtUtc: string | null;
  sentAtUtc: string | null;
  sentBody: string | null;
  /** The model that wrote the composed passage, or null when the fixed wording
   *  did. Null is meaningful, not missing. */
  aiModel: string | null;
  canSend: boolean;
  /** Why a Phoenix send is unavailable, phrased as the next action. Resolved
   *  server-side, so the button's enabled state and the endpoint's refusal come
   *  from one rule. Never compose a second reason client-side. */
  blockedReason: string | null;
  /** Whether taking the copy may be RECORDED. False only when the listing
   *  carries no broker at all: a send has to have gone to somebody. */
  canRecordCopySend: boolean;
  copySendBlockedReason: string | null;
  /** Why the message carries no prequalification line, or null when it carries
   *  one. Six distinct reasons server-side, because "not included" with no
   *  cause cannot tell a fixable listing from an unfixable buyer. */
  capacityNote: string | null;
  /** 🔴 LOCKED TRAILERS, both of them. Appended to every send by the transport
   *  and shown so a specialist reads what actually goes out. Neither is ever
   *  part of the editable body, and neither may be edited: the role statement
   *  is a regulated claim, and the signature is resolved from the acting user's
   *  own profile. */
  signatureBlock: string | null;
  roleStatement: string;
  /** WSA — the locked "Here is the listing I'm writing about: …" line as it
   *  will go out (after the message, before the sign-off), read from the
   *  listing now; null when the listing has no URL that qualifies for an email. */
  listingLinkLine?: string | null;
  /** WSA — the line's wording before its URL, to recognise the line inside a
   *  sent body whose URL has since changed. */
  listingLinkLead?: string | null;
}

export interface UpdateBrokerIntroEmailPayload {
  /** The whole body, not a patch. */
  body: string;
}

export interface SendBrokerIntroEmailResultDto {
  toEmail: string;
  subject: string;
  sentAtUtc: string;
  /** Reported SEPARATELY from the send: the email is irreversible and the log
   *  entry is not, so a bookkeeping failure must never read as a send
   *  failure. */
  communicationLogged: boolean;
  state: BrokerIntroEmailStateDto;
}

export interface BrokerIntroEmailDownloadDto {
  subject: string;
  /** The editable body alone — no trailers. */
  body: string;
  /** 🔴 Body, signature and role statement, assembled exactly as the broker
   *  will read them. THIS is what the copy button puts on the clipboard: no
   *  mail client appends Phoenix's role statement, so a copy that dropped it
   *  would be the one path by which this email reaches a broker without it. */
  copyText: string;
  /** Null when no address is on file — which does NOT block this path. */
  toEmail: string | null;
  /** The composer state. Requesting the copy records nothing (R4). */
  state: BrokerIntroEmailStateDto;
}

// ---------------------------------------------------------------------------
// Linked prequalification (PR 2c)
// ---------------------------------------------------------------------------

export interface PrequalificationCandidateDto {
  submissionId: string;
  referenceNumber: string | null;
  fullName: string;
  email: string;
  phone: string | null;
  outcome: string;
  qualifiedAmount: number | null;
  submittedAtUtc: string;
  /** Shown rather than hidden: linking a prequalification with no letter leaves
   *  the package still blocked, and the specialist should know before clicking. */
  hasLetter: boolean;
  alreadyLinkedToBuyerName: string | null;
  alreadyLinkedToBuyerId: string | null;
  matchReason: string;
  /** Populated only on the SELLER candidate list: another listing already names
   *  this prequalification as its seller's. Warned about, never refused — one
   *  seller genuinely can be marketing two businesses. */
  alreadyLinkedToListingName?: string | null;
  alreadyLinkedToListingId?: string | null;
  /** Whether the engine sized this submission at all.
   *
   *  🔴 NOT the same as `qualifiedAmount != null`. That field is REDACTED to
   *  null for every caller without `Prequalification.Read`, so on a narrowed
   *  row only this flag can still answer the question — and it is load-bearing:
   *  a blank amount would otherwise read as "not qualified" and send a
   *  specialist hunting for a prequalification that is not missing. */
  hasQualifiedAmount?: boolean;

}

// ---------------------------------------------------------------------------
// Biography (PR 2d)
// ---------------------------------------------------------------------------

/**
 * The thirteen structured biography fields.
 *
 * 🔴 There is deliberately no age, life-stage, marital-status, national-origin,
 * religion, sex or disability key, and there must never be one: this record
 * feeds an underwriting decision and those are ECOA / Regulation B protected
 * classes. What the business means by "life stage" is
 * `availabilityAndCommitment`.
 *
 * The rendering ORDER and the labels live in `pages/buyers/biographyFields.ts`,
 * not here — that is presentation, and a runtime export in this module is
 * invisible to every test that stubs it.
 */
export type BiographyFieldKey =
  | "professionalSummary"
  | "industryExperience"
  | "leadershipExperience"
  | "operationsExperience"
  | "businessOwnershipExperience"
  | "education"
  | "certificationsAndLicences"
  | "militaryService"
  | "notableAchievements"
  | "transferableSkills"
  | "geographicTies"
  | "acquisitionMotivation"
  | "availabilityAndCommitment";

/** The thirteen fields as a plain map — what the form edits and what a save
 *  sends. */
export type BiographyFieldValues = Partial<Record<BiographyFieldKey, string | null>>;

export interface BuyerLeadBiographyDto extends BiographyFieldValues {
  /** Null when source text exists but nothing has been saved yet. */
  id: string | null;
  rawText: string | null;
  rawTextUpdatedAtUtc: string | null;
  rawTextUpdatedByUserId: string | null;
  aiModel: string | null;
  aiAnalysedAtUtc: string | null;
  aiRawJson: string | null;
  reviewedAtUtc: string | null;
  reviewedByUserId: string | null;
  /** 🔴 The package gate — only a reviewed biography reaches a listing broker. */
  isReviewed: boolean;
  /** What a save did, set on a save's response only (every read leaves it
   *  null). The message a save shows is built from this, never assumed. */
  saveOutcome?: BuyerBiographySaveOutcome | null;
}

/**
 * What a biography save did — the server decides, the client says it.
 *
 * - `Unchanged`: no field and no source text differed; nothing was written and
 *   the review was not re-stamped.
 * - `SourceTextOnly`: only the source text changed; saved and stamped.
 * - `FieldsChanged`: a field changed; saved and stamped.
 */
export type BuyerBiographySaveOutcome = "Unchanged" | "SourceTextOnly" | "FieldsChanged";

/** What Analyse returns: a draft to confirm, never a save. */
export interface BuyerBiographyExtractionResultDto extends BiographyFieldValues {
  modelUsed: string | null;
  rawJson: string | null;
  /**
   * The server's sentence for the review step when the text looks like it is
   * about someone other than the buyer on file, or when the record holds no
   * name and the text was read as a named person's background. A warning, not a
   * refusal: the fields came back and nothing is saved until Save.
   */
  subjectNotice?: string | null;
}

export interface UpsertBuyerLeadBiographyPayload extends BiographyFieldValues {
  /** Null leaves the source text alone; "" clears it. */
  rawText?: string | null;
  aiModel?: string | null;
  aiRawJson?: string | null;
  aiAnalysedAtUtc?: string | null;
}

export interface BuyerPrequalificationLinkDto {
  submissionId: string | null;
  referenceNumber: string | null;
  outcome: string | null;
  qualifiedAmount: number | null;
  submittedAtUtc: string | null;
  hasLetter: boolean;
  linkedAtUtc: string | null;
  linkedByUserId: string | null;
  canGeneratePackage: boolean;
  /** Whether the engine sized this submission at all.
   *
   *  🔴 NOT the same as `qualifiedAmount != null`. That field is REDACTED to
   *  null for every caller without `Prequalification.Read`, so on a narrowed
   *  row only this flag can still answer the question — and it is load-bearing:
   *  a blank amount would otherwise read as "not qualified" and send a
   *  specialist hunting for a prequalification that is not missing. */
  hasQualifiedAmount?: boolean;
  /** How the linked submission was completed; a phone call shows "Completed by
   *  phone — certified by {name}, {date}". `certifiedAtUtc` is an INSTANT. */
  completionMode?: PrequalSubmissionCompletionMode | null;
  certifiedByName?: string | null;
  certifiedAtUtc?: string | null;
}

/**
 * ONE prequalification this buyer completed — a row of the attempts list and an
 * option in the package letter-override picker.
 *
 * `listingBusinessName` is the reason the attempts collection is a Buyer-module
 * table: the picker MUST show what each letter was sized against, and a
 * prequalification will never know what a listing is. Null is the normal case,
 * not a gap — prequalifying before finding a business is the usual first step.
 *
 * `unavailable` marks an attempt whose submission has been purged by
 * prequalification's own retention clock. Rendered as such, never dropped.
 */
export interface BuyerPrequalificationAttemptDto {
  id: string;
  submissionId: string;
  linkedAtUtc: string;
  source: string;
  isPrimary: boolean;
  unavailable: boolean;
  referenceNumber?: string | null;
  outcome?: string | null;
  qualifiedAmount?: number | null;
  submittedAtUtc?: string | null;
  hasLetter: boolean;
  listingId?: string | null;
  listingBusinessName?: string | null;
  /** The listing's canonical label — see {@link ListingSummaryDto.displayName}.
   *  Null when the attempt names NO listing, which is the normal first step and
   *  a different fact from a listing with no name. */
  listingDisplayName?: string | null;
  /** Whether the engine sized this submission at all.
   *
   *  🔴 NOT the same as `qualifiedAmount != null`. That field is REDACTED to
   *  null for every caller without `Prequalification.Read`, so on a narrowed
   *  row only this flag can still answer the question — and it is load-bearing:
   *  a blank amount would otherwise read as "not qualified" and send a
   *  specialist hunting for a prequalification that is not missing. */
  hasQualifiedAmount?: boolean;
}

/**
 * Invite this buyer to the funnel. Every field is optional and defaults to the
 * buyer's own record — re-typing a name already on screen is how a typo reaches
 * a recipient. An override invites a different address WITHOUT rewriting the
 * buyer.
 *
 * `confirmDuplicate` defaults false so the FIRST attempt always gets the
 * warning; the surface re-submits with true after the sender confirms.
 */
export interface SendBuyerPrequalInvitePayload {
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  confirmDuplicate?: boolean;
}

// ---------------------------------------------------------------------------
// The SELLER's prequalification (Escalation PR-4)
// ---------------------------------------------------------------------------

/**
 * The seller prequalification linked to the LISTING a submission is against.
 *
 * 🔴 **Listing-scoped, and the shape says so.** A seller prequalification is a
 * fact about the business, not about this buyer's offer for it, so linking from
 * one submission serves every other buyer submitted against the same listing.
 * `listingBusinessName` and `otherSubmissionCount` exist so the panel can state
 * that plainly — a specialist who thinks they are editing their own submission
 * is the misunderstanding this shape prevents.
 *
 * There is no `canGeneratePackage` twin: a seller prequalification gates
 * nothing. It produces a warning at escalation, because a seller
 * prequalification depends on a third party Phoenix cannot compel.
 */
export interface ListingSellerPrequalificationDto {
  listingId: string;
  listingBusinessName: string | null;
  submissionId: string | null;
  referenceNumber: string | null;
  outcome: string | null;
  submittedAtUtc: string | null;
  linkedAtUtc: string | null;
  linkedByUserId: string | null;

  /**
   * How many submissions sit against this listing OTHER THAN the one this was
   * addressed through.
   *
   * 🔴 Submission-relative, so only meaningful at the SUBMISSION address. It is
   * what lets the submission-side summary state the shared reach concretely —
   * "also serves 2 other buyers" — which is exactly the warning a reader needs
   * before following the link to a listing surface whose edits reach those
   * buyers. Read from a listing there is no addressed submission to exclude, so
   * it equals {@link submissionCount} and carries nothing the listing surface
   * should render.
   */
  otherSubmissionCount: number;

  /** How many submissions sit against this listing in total. Well-defined from
   *  BOTH addresses, which is why it exists beside `otherSubmissionCount`: the
   *  listing surface needs a count and "other" has no referent there. */
  submissionCount?: number;

  /** The listing's canonical label — see {@link ListingSummaryDto.displayName}.
   *  On the wire because every seller-prequalification surface printed
   *  `listingBusinessName` raw and so fell out of the ladder entirely. */
  listingDisplayName?: string | null;
}

/**
 * Invite this listing's seller to the funnel.
 *
 * `email` is REQUIRED, unlike the buyer payload where every field defaults: the
 * buyer module holds no seller contact record at all, so there is nothing to
 * default from. Names are optional and fall back to a neutral greeting.
 */
export interface SendSellerPrequalInvitePayload {
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  confirmDuplicate?: boolean;
}

// ---------------------------------------------------------------------------
// Listing + submission writes
// ---------------------------------------------------------------------------

export interface UpsertListingAgentPayload {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  firmName?: string | null;
  title?: string | null;
  city?: string | null;
  state?: string | null;
  brokerId?: string | null;
  notes?: string | null;
  /**
   * STRICT create: refuse with a 409 (`details.code` =
   * {@link LISTING_AGENT_ALREADY_ON_FILE_CODE}) instead of reusing an agent
   * already on file, filling its blanks or merging duplicates. Sent by the New
   * agent form in every context; nothing else sends it. Ignored by an update.
   */
  rejectExisting?: boolean;
  /** D20. On a create, absent is "Unknown"; on an update, absent is unchanged. */
  phoneType?: ListingAgentPhoneType;
  /** D19 — create a firm's general contact. Sent only by the New agent form's
   *  "Firm contact (no named agent)" checkbox and a research save. Ignored by
   *  an update: the flag is never changed after creation. */
  isFirmContact?: boolean;
}

export interface ListingWritePayload {
  rawUrl?: string | null;
  businessName?: string | null;
  headline?: string | null;
  description?: string | null;
  industry?: string | null;
  marketplaceName?: string | null;
  listingReference?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  askingPrice?: number | null;
  cashFlow?: number | null;
  revenue?: number | null;
  inventory?: number | null;
  realEstateValue?: number | null;
  yearEstablished?: number | null;
  employeeCount?: number | null;
  listingAgentId?: string | null;
  notes?: string | null;
}

/** Create takes an inline `newAgent` because an agent is a SHARED record
 *  deduped by email — the server get-or-creates, which a flattened agent block
 *  could not express. */
export interface CreateListingPayload extends ListingWritePayload {
  newAgent?: UpsertListingAgentPayload | null;
  confirmCreateDespiteDuplicate?: boolean;
}

export interface UpdateListingPayload extends ListingWritePayload {
  clearListingAgent?: boolean;
  /** D20 — the listing broker's web contact form. Absent = unchanged, "" = clear. */
  contactFormUrl?: string | null;
}

/** Mirrors the backend `ListingDuplicateDto` record field for field. It drifted
 *  once — declaring a `matchedOn` the server had no column for while omitting
 *  the `rawUrl` and `submissionCount` it did send — so keep the two in step. */
export interface ListingDuplicateDto {
  id: string;
  businessName?: string | null;
  rawUrl?: string | null;
  marketplaceName?: string | null;
  city?: string | null;
  state?: string | null;
  askingPrice?: number | null;
  submissionCount: number;
  createdAt: string;
  /** Which signal collided: "listing URL", "business name", or both. Rendered
   *  verbatim — a duplicate claim is only arguable if you can see what matched. */
  matchedOn: string;
  /** The candidate's canonical label — see {@link ListingSummaryDto.displayName}.
   *  A warning that names a row differently from its own page is unactionable. */
  displayName?: string;
}

export interface ListingDuplicateCheckDto {
  hasMatches: boolean;
  /** Null on a name-only probe: there is no URL key to have compared. */
  normalizedUrl?: string | null;
  matches: ListingDuplicateDto[];
}

export interface ListingExtractionResultDto {
  businessName?: string | null;
  headline?: string | null;
  description?: string | null;
  industry?: string | null;
  marketplaceName?: string | null;
  listingReference?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  askingPrice?: number | null;
  cashFlow?: number | null;
  revenue?: number | null;
  inventory?: number | null;
  realEstateValue?: number | null;
  yearEstablished?: number | null;
  employeeCount?: number | null;
  /** Extracted into its OWN block rather than flattened — see `newAgent`. */
  agent?: UpsertListingAgentPayload | null;
  notesSummary?: string | null;
  modelUsed?: string | null;
}

export interface ListingAgentListParams {
  page?: number;
  pageSize?: number;
  /**
   * 🔴 `q`, NOT `search`. The server binds this endpoint's free-text term from
   * `PagedQuery.Q` — every paged query in the API does — and a field named
   * anything else is simply not bound. It was declared as `search` here, so the
   * directory's search box sent a parameter the server had never heard of and
   * silently returned the unfiltered first page: typing narrowed nothing, and
   * because the response was a valid 200 with rows in it, the box looked like it
   * was working on a small dataset and like the agent did not exist on a large
   * one. Matches `ListingListParams.q`, which was always right.
   */
  q?: string;
  state?: string;
  firmName?: string;
  escalated?: boolean;
}

export interface CreateBuyerSubmissionPayload {
  buyerLeadId: string;
  listingId: string;
  askingPriceAtSubmission?: number | null;
  notes?: string | null;
  nextFollowUpDate?: string | null;
  // 🔴 confirmCreateDespiteDuplicate ("present anyway") is GONE (ruling R1): a
  // presented pair is refused with 409 `submissionAlreadyPresented`, no override.
}

/** The offer structure, plus the follow-up. Bare `undefined` = unchanged. */
export interface UpdateBuyerSubmissionPayload {
  askingPriceAtSubmission?: number | null;
  proposedOfferPrice?: number | null;
  downPaymentAmount?: number | null;
  downPaymentPercent?: number | null;
  sbaLoanAmount?: number | null;
  sellerNoteAmount?: number | null;
  sellerNoteTerms?: string | null;
  workingCapital?: number | null;
  contingencies?: string | null;
  targetCloseDate?: string | null;
  transitionPeriod?: string | null;

  /** How much of the program fee the buyer carries: 0, 25, 50, 75 or 100.
   *
   *  🔴 Omit to leave the stored answer alone. There is no clear verb: a save
   *  through this form marks the offer complete, so a specialist correcting a
   *  close date on an offer whose fee question was never asked must not have it
   *  recorded as the buyer declining. */
  programFeeCoveragePercent?: number;
  // 🔴 `ndaAuthorizationNote` was REMOVED here in lockstep with the server
  // dropping it from `UpdateBuyerSubmissionRequest` (WS4). The note is now an
  // append-only ENTRY on the NDA record's current version, written through
  // `recordNda` or the entry endpoints — a dated, attributed line rather than
  // one column any save could silently rewrite. Nothing ever sent it from here.
  nextFollowUpDate?: string | null;
  clearNextFollowUpDate?: boolean;
  notes?: string | null;

  /**
   * When the buyer's financials reached the listing side — rung 6 of the
   * submission's ten-rung milestone ladder.
   *
   * <p>🔴 <b>The stamp had no writer at all,</b> so that rung could never be
   * reached by any path through the product: the server comment claimed a detail
   * form field existed for it and none did. This is that field.</p>
   *
   * <p><b>No `clear` companion, deliberately.</b> Milestone stamps are
   * forward-only by design (`??= now`) — a submission that later dies must not
   * revoke credit already earned — so "un-receive the financials" is not a state
   * the ladder has.</p>
   */
  financialsReceivedAtUtc?: string | null;
}

export interface SetBuyerSubmissionStatusPayload {
  status: BuyerSubmissionStatus;
  reason?: string | null;
}

export interface BuyerSubmissionStatsDto {
  total: number;
  live: number;
  terminal: number;
  overdueFollowUp: number;
  underContract: number;
  byStatus: Record<string, number>;
  /** Attention — outreach sent, broker silent past the window. Live only. */
  awaitingBrokerResponse: number;
  /** Attention — drafted and never sent. Generation is off the send path, so
   *  this is a state a specialist genuinely occupies for days. */
  packageGeneratedNotSent: number;
  /** Attention — buyer approved, NDA neither signed nor recorded as not
   *  required. A recorded "the broker does not use one" is NOT counted. */
  ndaOutstanding: number;
  /** Attention — no conversation logged inside the window. */
  noRecentActivity: number;
  /** Velocity — median days a LIVE submission has sat since its newest
   *  milestone stamp (or since it was created). There is no status-change
   *  history in this module, so a literal time-in-status cannot be computed;
   *  `modifiedAt` is the other candidate and bumps on a typo fix. */
  medianDaysSinceLastMilestone: number | null;
  /** The median's sample size. A median over three rows and one over three
   *  hundred are different claims. */
  medianSampleSize: number;
  createdByMonth: MonthlyCountPoint[];
}

/** Ruling R1: every non-deleted submission for the pair (any status, archived
 *  included). `hasMatches` means the present is REFUSED — there is no override. */
export interface BuyerSubmissionDuplicateCheckDto {
  hasMatches: boolean;
  matches: BuyerSubmissionSummaryDto[];
}

/** The standing submission for a (buyer, listing) pair — what refuses a new
 *  presentation (R1). Furthest along, then newest. */
export interface BuyerSubmissionPresentationDto {
  submissionId: string;
  buyerLeadId: string;
  listingId: string;
  status: BuyerSubmissionStatus;
  isArchived: boolean;
  archivedAtUtc?: string | null;
}

export type BuyerSubmissionBulkOutcome = "Succeeded" | "Skipped" | "Failed";

export interface BuyerSubmissionBulkItemDto {
  submissionId: string;
  listingDisplayName: string;
  outcome: BuyerSubmissionBulkOutcome;
  reason?: string | null;
}

export interface BuyerSubmissionBulkResultDto {
  items: BuyerSubmissionBulkItemDto[];
  succeeded: number;
  skipped: number;
  failed: number;
}

export interface BuyerBulkPresentItemDto {
  listingId: string;
  listingDisplayName: string;
  outcome: BuyerSubmissionBulkOutcome;
  reason?: string | null;
  submissionId?: string | null;
  existing?: BuyerSubmissionPresentationDto | null;
}

export interface BuyerBulkPresentResultDto {
  items: BuyerBulkPresentItemDto[];
  presented: number;
  skipped: number;
  failed: number;
}

/** The code `BuyerSubmissionsController.Create` puts in `details.code` on a 409
 *  — `BuyerSubmissionAlreadyPresentedException.ErrorCode`. */
export const SUBMISSION_ALREADY_PRESENTED_CODE = "submissionAlreadyPresented";

/** The standing submission a refused present named (R1), or null when the error
 *  is anything else. Branch on the code, never on the prose. */
export function submissionAlreadyPresented(e: unknown): BuyerSubmissionPresentationDto | null {
  if (!(e instanceof ApiError) || e.status !== 409) return null;
  const d = e.details as { code?: unknown; existing?: unknown } | null | undefined;
  if (d?.code !== SUBMISSION_ALREADY_PRESENTED_CODE) return null;
  const x = d.existing as Partial<BuyerSubmissionPresentationDto> | null | undefined;
  return x && typeof x.submissionId === "string" ? (x as BuyerSubmissionPresentationDto) : null;
}

export interface BuyerAssignmentGrantDto {
  id: string;
  specialistUserId: string;
  specialistDisplayName?: string | null;
  buyerLeadId: string;
  buyerDisplayName: string;
  grantedAtUtc: string;
  method: BuyerAssignmentMethod;
  grantedByUserId?: string | null;
  grantedByDisplayName?: string | null;
  entitlementAtGrant?: number | null;
  qualifyingCountAtGrant?: number | null;
  correctionReason?: string | null;
}

export interface BuyerAssignmentGrantListParams {
  page?: number;
  pageSize?: number;
  specialistUserId?: string;
  buyerLeadId?: string;
  from?: string;
  to?: string;
}

// ---------------------------------------------------------------------------
// CSV import (WS-6)
// ---------------------------------------------------------------------------

/**
 * One rejected row. Duplicates are NOT here — this module creates and flags a
 * suspected duplicate rather than skipping it, so a duplicate row appears in
 * `flaggedDuplicateCount` and lands in the book.
 */
export interface BuyerImportRowErrorDto {
  row: number;
  message: string;
}

export interface BuyerImportResultDto {
  totalRows: number;
  createdCount: number;
  /** Created AND marked as a suspected duplicate. Never skipped. */
  flaggedDuplicateCount: number;
  rejectedCount: number;
  fileName: string;
  errors: BuyerImportRowErrorDto[];
}

// ---------------------------------------------------------------------------
// Associated listings (WS-3)
// ---------------------------------------------------------------------------

/**
 * One row of a buyer's associated-listings list. Mirrors the backend
 * `BuyerListingAssociationRowDto` field for field.
 *
 * 🔴 `submissionStatus` is DERIVED from the attached submission, never stored on
 * the association — the copy that drifts is the one the list renders. Both
 * submission fields are null together when this buyer has no submission to this
 * listing, which means NOT YET WORKED and is a distinct state from any status
 * value. Rendering that as a status token would claim a funnel position nobody
 * has reached.
 */
export interface BuyerListingAssociationRowDto {
  id: string;
  listingId: string;
  listingBusinessName?: string | null;
  listingAskingPrice?: number | null;
  listingCity?: string | null;
  listingState?: string | null;
  listingIndustry?: string | null;
  note?: string | null;
  createdAt: string;
  /** The submission the badge is derived from — the furthest-along one, newest
   *  first on a tie. Null when the buyer has no submission to this listing. */
  submissionId?: string | null;
  /** Null means "not yet worked", NOT a submission in some starting state. */
  submissionStatus?: BuyerSubmissionStatus | null;
  /** The listing's canonical label, resolved SERVER-side — this row carries
   *  `listingBusinessName` and no other name column, so a shortlisted BLIND
   *  listing could only ever render as the placeholder. */
  listingDisplayName?: string;
  /** Addendum B (B1): the listing's agent, for the Listing agent column and
   *  this tab's client-side search. */
  listingAgent?: ListingAgentBriefDto | null;
}

// ---------------------------------------------------------------------------
// Potential Deal Structure (document 3) — staff only
// ---------------------------------------------------------------------------

/** Mirrors `DealStructureCashFlowType` (string enum on the wire). */
export type DealStructureCashFlowType = "FreeCashFlow" | "Ebitda" | "Sde";

/** Mirrors `DealStructurePriceBasis`. */
export type DealStructurePriceBasis = "Asking" | "Supported" | "Custom";

/** Mirrors `DealStructureDownPaymentSource`. STAFF-ONLY: never printed. */
export type DealStructureDownPaymentSource = "Prequalification" | "Assessment" | "Staff";

/** Mirrors `DealStructureCashFlowSource`. */
export type DealStructureCashFlowSource = "Listing" | "Phoenix";

/** Mirrors `BuyerDealStructureStatus`. */
export type BuyerDealStructureStatus = "NotBuilt" | "InProgress" | "Complete";

/** A refusal or a staff warning. `message` is the sentence to show. */
export interface BuyerDealStructureIssueDto {
  code: string;
  message: string;
}

/** Every computed figure, from the server's `DealStructureCalculator` over the
 *  saved inputs. Fractions are 0–1; the client formats them and computes
 *  nothing. */
export interface BuyerDealStructureFiguresDto {
  askingPrice?: number | null;
  askingAboveMaximum: boolean;
  effectiveCashFlow?: number | null;
  supportedPrice?: number | null;
  supportedPriceCapped: boolean;
  price?: number | null;
  downPayment?: number | null;
  financed?: number | null;
  balance?: number | null;
  downPaymentFraction?: number | null;
  financedFraction?: number | null;
  financedMultiple?: number | null;
  coverage?: number | null;
  amortizationYears: number;
  transitionRate: number;
  poolRate: number;
  transitionMonthly?: number | null;
  poolMonthly?: number | null;
  firstYearCash?: number | null;
  vestingBalance?: number | null;
  errors: BuyerDealStructureIssueDto[];
  warnings: BuyerDealStructureIssueDto[];
  missing: string[];
  isComplete: boolean;
  /** The 1 November 2026 schedules (D7). The headline `financed` / `balance` /
   *  `coverage` above are the 20-year schedule's; the seller-panel members
   *  (`transitionMonthly` … `vestingBalance`) are null. */
  twentyYear?: BuyerDealStructureScheduleDto | null;
  tenYear?: BuyerDealStructureScheduleDto | null;
}

/** One schedule of a deal structure. Mirrors `BuyerDealStructureScheduleDto`.
 *  `originationCoverage` is staff-only (D2) and shown without a flag. */
export interface BuyerDealStructureScheduleDto {
  amortizationMonths: number;
  maximumFinanced: number;
  financed: number;
  balance: number;
  financedMultiple?: number | null;
  sizingCoverage?: number | null;
  originationCoverage?: number | null;
  belowMinimum: boolean;
}

/** Why the Suggested card shows no supported price. Mirrors
 *  `BuyerDealStructureSupportedUnavailable`. */
export type BuyerDealStructureSupportedUnavailable =
  | "typeUnknown"
  | "sde"
  | "noCashFlow"
  | "noDownPayment";

/** The deterministic Suggested structure and the wizard's prefills. No AI:
 *  every figure is read from the listing, the prequalification or the
 *  assessment, with its source. */
export interface BuyerDealStructureSuggestionDto {
  askingPrice?: number | null;
  /** True: the asking price at submission; false: the listing's live one. */
  askingPriceFromSubmission: boolean;
  listingCashFlow?: number | null;
  /** Null when the listing records no type (only a Research listing does). */
  listingCashFlowType?: DealStructureCashFlowType | null;
  downPayment?: number | null;
  downPaymentSource?: DealStructureDownPaymentSource | null;
  /** An instant: the prequalification's completion or the assessment's. */
  downPaymentSourceDate?: string | null;
  supportedPrice?: number | null;
  supportedPriceCapped: boolean;
  supportedPriceUnavailableReason?: BuyerDealStructureSupportedUnavailable | null;
}

/** The Potential Deal Structure on a submission, as staff see it. */
export interface BuyerDealStructureDto {
  submissionId: string;
  status: BuyerDealStructureStatus;
  /** The wizard's resume step. */
  step?: number | null;
  completedAtUtc?: string | null;
  reference?: string | null;
  cashFlow?: number | null;
  cashFlowType?: DealStructureCashFlowType | null;
  cashFlowSource?: DealStructureCashFlowSource | null;
  freeCashFlowEstimate?: number | null;
  downPayment?: number | null;
  downPaymentSource?: DealStructureDownPaymentSource | null;
  priceBasis?: DealStructurePriceBasis | null;
  /** Present only on the Custom basis. */
  customPrice?: number | null;
  note?: string | null;
  figures: BuyerDealStructureFiguresDto;
  suggested: BuyerDealStructureSuggestionDto;
  canEdit: boolean;
}

/**
 * PATCH one wizard step, or the whole EDIT modal. Absent means unchanged.
 *
 * <p>A refused figure is a 400 whose message is the sentence to show staff;
 * nothing is saved. `confirm` saves the structure complete (forward-only).</p>
 */
export interface UpdateBuyerDealStructurePayload {
  step?: number;
  cashFlow?: number;
  cashFlowType?: DealStructureCashFlowType;
  /** Read only when the resulting type is SDE. */
  freeCashFlowEstimate?: number;
  downPayment?: number;
  priceBasis?: DealStructurePriceBasis;
  /** Read only when the resulting basis is Custom. */
  customPrice?: number;
  /** At most 300 characters; `""` clears it. */
  note?: string;
  confirm?: boolean;
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

export const buyersApi = {
  list: (params: BuyerLeadListParams) =>
    api.get<PagedResult<BuyerLeadSummaryDto>>("/buyers", params),
  stats: (params: BuyerLeadListParams) =>
    api.get<BuyerLeadStatsDto>("/buyers/stats", params),
  get: (id: string) => api.get<BuyerLeadDetailDto>(`/buyers/${id}`),

  /** The buyer lands UNASSIGNED unless the request names an owner — the same
   *  posture as every other intake path, including the funnel. */
  create: (body: CreateBuyerLeadPayload) =>
    api.post<BuyerLeadDetailDto>("/buyers", body),

  /** Partial edit. Omitted fields keep their current value. */
  update: (id: string, body: UpdateBuyerLeadPayload) =>
    api.patch<BuyerLeadDetailDto>(`/buyers/${id}`, body),

  /** D4 — the buyer's "Referred by" and the D9 suggestion. */
  getReferredBy: (id: string) => api.get<BuyerReferredByDto>(`/buyers/${id}/referred-by`),

  /** D4 — set (broker + fee) or, with a null broker, clear "Referred by". */
  setReferredBy: (id: string, brokerId: string | null, feePercent: number | null) =>
    api.put<BuyerReferredByDto>(`/buyers/${id}/referred-by`, { brokerId, feePercent }),

  /**
   * Read a pasted inquiry into a PREFILL. Persists nothing — the specialist
   * confirms and edits, then `create` writes. Throws `ApiError` 422 when the
   * text cannot be read or the daily AI cost ceiling is reached.
   */
  pasteExtract: (rawText: string) =>
    api.post<BuyerLeadExtractionResultDto>("/buyers/paste-extract", { rawText }),

  setStatus: (id: string, body: SetBuyerLeadStatusPayload) =>
    api.post<BuyerLeadDetailDto>(`/buyers/${id}/status`, body),

  /**
   * Archive a buyer — hidden from the working surfaces, deleted from nothing.
   * Reason is required (min 10 chars, enforced server-side too).
   */
  archive: (id: string, body: ArchiveBuyerLeadPayload) =>
    api.post<BuyerLeadDetailDto>(`/buyers/${id}/archive`, body),

  /** Bring one back. Reason required. Does NOT restore a status the archive
   *  may have changed — there is no stored previous status to restore. */
  unarchive: (id: string, body: { reason: string }) =>
    api.post<BuyerLeadDetailDto>(`/buyers/${id}/unarchive`, body),

  /** 🔴 Buyers.ManageAll, enforced SERVER-SIDE — hiding the button is not the
   *  control. Selection is page-scoped; the caller says so in words. */
  bulkArchive: (body: BulkArchiveBuyerLeadsPayload) =>
    api.post<BulkArchiveBuyerLeadsResultDto>("/buyers/bulk-archive", body),

  /** The education email's preview state. A READ — opening the preview sends
   *  nothing, which is the whole point of the confirmation step. */
  getEducationEmail: (id: string) =>
    api.get<BuyerEducationEmailStateDto>(`/buyers/${id}/education-email`),

  /** Send it. Subject and body are what the specialist confirmed, possibly
   *  edited — they ride the request rather than being recomposed server-side. */
  sendEducationEmail: (id: string, body: { subject?: string; body?: string }) =>
    api.post<SendBuyerEducationEmailResultDto>(`/buyers/${id}/education-email/send`, body),

  /** Record that the specialist sent it themselves. Stamps the same columns
   *  and dispatches no mail. */
  markEducationEmailSent: (id: string, body: { body?: string }) =>
    api.post<BuyerEducationEmailStateDto>(`/buyers/${id}/education-email/mark-sent`, body),

  /**
   * A fresh link to the brochure this email attaches, for the paths Phoenix
   * does not send on.
   *
   * 🔴 Called at CLICK time and never cached: a SAS expires, and a stale one
   * handed out of a query cache is a broken download. It is gated on
   * Buyers.Update rather than the brochure module's own admin-only permission,
   * which the buyer roles do not hold.
   */
  getEducationBrochureLink: (id: string) =>
    api.get<ProgramBrochureLinkDto>(`/buyers/${id}/education-email/brochure-link`),

  /**
   * The branded HTML the recipient will actually receive, for the send-preview
   * pane. Sends nothing and records nothing.
   *
   * 🔴 Rendered by the SERVER, through the same composition the send runs. The
   * shell is never rebuilt in TypeScript — a second copy of the design is exactly
   * how the preview came to disagree with the delivered email.
   */
  previewOutreach: (id: string, body: { subject: string; body: string }) =>
    api.post<{ html: string }>(`/buyers/${id}/outreach/preview`, body),

  // -------------------------------------------------------------------------
  // The qualification interview
  //
  // The specialist records what the buyer SAID; the server computes the
  // verdicts and reveals them only on completion. There is no write path for a
  // verdict anywhere in this block except the explicit override.
  // -------------------------------------------------------------------------

  /** The questions, served. Buyer-agnostic. With `wordingVersion` the
   *  registry is rendered in that published wording version (0 = the coded
   *  defaults); without it, in the wording in force now. */
  getInterviewRegistry: (wordingVersion?: number) =>
    api.get<BuyerInterviewRegistryDto>(
      "/buyers/qualification-interview/registry",
      wordingVersion === undefined ? undefined : { wordingVersion },
    ),

  /**
   * The interview in progress, else the newest completed one.
   *
   * 🔴 <b>404 when none has been conducted, and that is a STATE rather than an
   * error.</b> Callers must branch on it — an unassessed buyer is the normal
   * case, not a failure.
   */
  getInterview: (id: string) =>
    api.get<BuyerInterviewDto>(`/buyers/${id}/qualification-interview`),

  /** Start one, or resume the one in progress. Idempotent — the button says
   *  "Start" or "Resume", the endpoint does not care which. */
  startInterview: (id: string) =>
    api.post<BuyerInterviewDto>(`/buyers/${id}/qualification-interview`, {}),

  /** Autosave one answer. One call per answer, deliberately. */
  saveInterviewAnswer: (id: string, body: SaveBuyerInterviewAnswerPayload) =>
    api.put<BuyerInterviewDto>(`/buyers/${id}/qualification-interview/answers`, body),

  /** Complete it and reveal the computed verdicts. Refused server-side while
   *  the spine is incomplete. */
  completeInterview: (id: string) =>
    api.post<BuyerInterviewDto>(`/buyers/${id}/qualification-interview/complete`, {}),

  /** Override the roll-up on a completed interview. The reason is required. */
  overrideInterviewVerdict: (id: string, body: OverrideBuyerInterviewVerdictPayload) =>
    api.post<BuyerInterviewDto>(`/buyers/${id}/qualification-interview/override`, body),

  /** Every interview conducted for this buyer, newest first. */
  listInterviews: (id: string) =>
    api.get<BuyerInterviewSummaryDto[]>(`/buyers/${id}/qualification-interviews`),

  /** One prior version, read-only — so a letter already sent can be traced to
   *  the answers behind it. */
  getInterviewVersion: (id: string, version: number) =>
    api.get<BuyerInterviewDto>(`/buyers/${id}/qualification-interviews/${version}`),

  /** Detect, warn, never block. Callers show the matches and let the specialist
   *  decide; nothing here refuses a create. */
  duplicateCheck: (params: { email?: string; phone?: string; excludeId?: string }) =>
    api.get<BuyerLeadDuplicateCheckDto>("/buyers/duplicate-check", params),

  /** Mint or link the Borrower party. One-way and guarded — a second call on an
   *  already-converted buyer is a 400, never a second party. */
  convert: (id: string, body: ConvertBuyerLeadPayload) =>
    api.post<ConvertBuyerLeadResultDto>(`/buyers/${id}/convert`, body),

  /** Bulk create from a CSV. Duplicates are CREATED AND FLAGGED, never skipped
   *  — deliberately unlike the Leads import. */
  importCsv: (
    file: File,
    defaultSource: BuyerLeadSource,
    options: {
      /** Assign every imported row to this specialist (§2.1). */
      assignToSpecialistUserId?: string | null;
      /** Attribute every imported row to this advertisement (§2.1). */
      advertisementId?: string | null;
    } = {},
  ) => {
    const form = new FormData();
    form.append("file", file);
    form.append("defaultSource", defaultSource);
    if (options.assignToSpecialistUserId) {
      form.append("assignToSpecialistUserId", options.assignToSpecialistUserId);
    }
    if (options.advertisementId) form.append("advertisementId", options.advertisementId);
    return api.upload<BuyerImportResultDto>("/buyers/import", form);
  },

  // Linked prequalification. Nothing here auto-links — candidates are a
  // suggestion the specialist confirms.
  getPrequalification: (id: string) =>
    api.get<BuyerPrequalificationLinkDto>(`/buyers/${id}/prequalification`),
  findPrequalificationCandidates: (id: string, search?: string) =>
    api.get<PrequalificationCandidateDto[]>(
      `/buyers/${id}/prequalification/candidates`,
      search ? { search } : undefined),
  linkPrequalification: (id: string, submissionId: string) =>
    api.post<BuyerPrequalificationLinkDto>(`/buyers/${id}/prequalification`, { submissionId }),
  unlinkPrequalification: (id: string) =>
    api.delete<BuyerPrequalificationLinkDto>(`/buyers/${id}/prequalification`),

  /** Every prequalification this buyer completed, newest first. */
  listPrequalificationAttempts: (id: string) =>
    api.get<BuyerPrequalificationAttemptDto[]>(`/buyers/${id}/prequalification/attempts`),

  // Invitations, scoped to one buyer. These are the BUYER-scoped endpoints
  // under Buyers.Read / Buyers.Update — deliberately NOT the staff surface on
  // PrequalificationController, which carries Prequalification.Manage and would
  // 403 for F1/F2.
  listPrequalificationInvites: (id: string, page = 1, pageSize = 25) =>
    api.get<PagedResult<PrequalInviteDto>>(
      `/buyers/${id}/prequalification/invites`, { page, pageSize }),
  sendPrequalificationInvite: (id: string, payload: SendBuyerPrequalInvitePayload = {}) =>
    api.post<PrequalInviteDto>(`/buyers/${id}/prequalification/invites`, payload),

  /** ⚠️ Resends to the INVITEE. Distinct from `PrequalInviteNudgeWorker`, which
   *  nudges the SENDER — different action, different recipient. */
  resendPrequalificationInvite: (id: string, inviteId: string) =>
    api.post<PrequalInviteDto>(
      `/buyers/${id}/prequalification/invites/${inviteId}/resend`, {}),

  // Biography (PR 2d). There is no GET — the block rides the detail record,
  // loaded with the parent like the qualification block.
  //
  // `analyse` returns a DRAFT and writes no biography row, so re-running it can
  // never destroy a specialist's saved edits. It keeps the pasted text on the
  // buyer ONLY while no biography is saved yet (a first paste, so a failed read
  // or a walk-away does not lose it); on a buyer with a biography the paste
  // becomes the source text only when somebody saves. A read the server cannot
  // use arrives as ApiError 422 carrying a sentence that names why — too long,
  // nothing found, several people, unreadable, unavailable, or the daily AI
  // cost ceiling.
  analyseBiography: (id: string, rawText: string) =>
    api.post<BuyerBiographyExtractionResultDto>(
      `/buyers/${id}/biography/analyse`, { rawText }),

  /** Saving IS the review — it stamps the reviewer, which is what makes the
   *  block package-eligible. The response's `saveOutcome` says what the save
   *  actually did; an all-empty biography is refused with a 400. */
  saveBiography: (id: string, body: UpsertBuyerLeadBiographyPayload) =>
    api.put<BuyerLeadBiographyDto>(`/buyers/${id}/biography`, body),

  // Associated listings (WS-3). The buyer's shortlist — everything being looked
  // at, whether or not it has been presented yet. Reads are Buyers.Read; the
  // three writes are Buyers.Update.
  listAssociatedListings: (id: string) =>
    api.get<BuyerListingAssociationRowDto[]>(`/buyers/${id}/associated-listings`),

  /** 🔴 A duplicate pair is REFUSED (400), deliberately unlike the submission's
   *  advisory duplicate warning: the same listing twice on one buyer's list is
   *  nothing but a duplicate row. */
  associateListing: (id: string, body: { listingId: string; note?: string | null }) =>
    api.post<BuyerListingAssociationRowDto>(`/buyers/${id}/associated-listings`, body),

  // The last two are addressed by ASSOCIATION id, not by buyer — the row is the
  // thing being edited, and the route says so.

  /** The note is the only editable field: the listing an association points at
   *  is the row's identity. */
  updateAssociatedListing: (associationId: string, body: { note?: string | null }) =>
    api.patch<BuyerListingAssociationRowDto>(
      `/buyers/associated-listings/${associationId}`, body),

  /** Soft-delete, and it touches nothing else — any submission the buyer already
   *  made to the listing survives. */
  removeAssociatedListing: (associationId: string) =>
    api.delete<void>(`/buyers/associated-listings/${associationId}`),
};

export const listingsApi = {
  list: (params: ListingListParams) =>
    api.get<PagedResult<ListingSummaryDto>>("/listings", params),

  /** Tile counts, over the SAME query as `list` — the contract the buyers and
   *  submissions stats endpoints already keep, and the reason the tiles above a
   *  table always describe the rows underneath it. */
  stats: (params: ListingListParams) =>
    api.get<ListingStatsDto>("/listings/stats", params),

  get: (id: string) => api.get<ListingDetailDto>(`/listings/${id}`),

  /** Create, optionally get-or-creating the agent in the same call. */
  create: (body: CreateListingPayload) =>
    api.post<ListingDetailDto>("/listings", body),
  update: (id: string, body: UpdateListingPayload) =>
    api.patch<ListingDetailDto>(`/listings/${id}`, body),

  /** Read a pasted business-for-sale listing into a PREFILL, agent included. */
  pasteExtract: (rawText: string) =>
    api.post<ListingExtractionResultDto>("/listings/paste-extract", { rawText }),

  /** Matches on the normalised marketplace URL as well as the name, which is
   *  why the response carries `normalizedUrl` back. */
  duplicateCheck: (params: { rawUrl?: string; businessName?: string; excludeId?: string }) =>
    api.get<ListingDuplicateCheckDto>("/listings/duplicate-check", params),

  // Agents. Shared records deduped by email. `upsertAgent` is the one create
  // verb: plain, it get-or-creates; with `rejectExisting` (the New agent form
  // only) it inserts or refuses with 409 `listingAgentAlreadyOnFile`.
  listAgents: (params: ListingAgentListParams) =>
    api.get<PagedResult<ListingAgentDto>>("/listings/agents", params),
  getAgent: (id: string) => api.get<ListingAgentDto>(`/listings/agents/${id}`),
  upsertAgent: (body: UpsertListingAgentPayload) =>
    api.post<ListingAgentDto>("/listings/agents", body),
  updateAgent: (id: string, body: UpsertListingAgentPayload) =>
    api.patch<ListingAgentDto>(`/listings/agents/${id}`, body),

  /**
   * Agents already on file that look like the one being added — by email
   * (ignoring case), phone (last ten digits) or name (ignoring case and extra
   * spaces). A READ: it writes nothing and audits nothing. POST only because
   * it takes the same body as the create.
   */
  checkAgentDuplicates: (body: UpsertListingAgentPayload) =>
    api.post<ListingAgentDuplicateCheckDto>("/listings/agents/duplicate-check", body),

  /**
   * Escalate a listing agent into broker recruitment — the prerequisite for
   * escalating any of their listings' submissions into a deal.
   *
   * The endpoint shipped without a client function or a button, so this action
   * has been API-only; it was also double-gated in a way that made it
   * unreachable outside ADMIN. Both are fixed in Escalation PR-2.
   */
  escalateAgent: (agentId: string, body: { reason?: string | null }) =>
    api.post<ListingAgentDto>(`/listings/agents/${agentId}/escalate`, body),

  // ---------------------------------------------------------------------
  // The SELLER's prequalification, addressed by the record it belongs to.
  //
  // 🔴 The same seven calls exist on `buyerSubmissionsApi`, and both stay.
  // They are NOT duplicates: a seller prequalification describes the seller
  // and the business, so the LISTING is where the surface that manages it
  // lives, and these are its calls. A specialist standing on a submission
  // still sends from there — that is genuinely the provenance of what they
  // send, and the invitation records it.
  //
  // ⚠️ THE TWO ADDRESSES CARRY DIFFERENT GATES, which is why the client keeps
  // them apart rather than resolving one into the other. These take
  // Listings.Read / Listings.Manage and no per-row check, matching every
  // other write on that controller; the submission ones take Submissions.*
  // plus the buyer's own ownership rule. Re-pointing a submission-side caller
  // at these would quietly widen who may send.
  // ---------------------------------------------------------------------

  getSellerPrequalification: (listingId: string) =>
    api.get<ListingSellerPrequalificationDto>(
      `/listings/${listingId}/seller-prequalification`),

  /** An empty search returns an empty list: there is no seller contact record
   *  to default from, and answering an unasked search with the whole book is a
   *  directory rather than a suggestion. */
  findSellerPrequalificationCandidates: (listingId: string, search?: string) =>
    api.get<PrequalificationCandidateDto[]>(
      `/listings/${listingId}/seller-prequalification/candidates`,
      search ? { search } : undefined),

  linkSellerPrequalification: (listingId: string, submissionId: string) =>
    api.post<ListingSellerPrequalificationDto>(
      `/listings/${listingId}/seller-prequalification`, { submissionId }),
  unlinkSellerPrequalification: (listingId: string) =>
    api.delete<ListingSellerPrequalificationDto>(
      `/listings/${listingId}/seller-prequalification`),

  /** 🔴 The UNION — invitations sent from this listing AND from every
   *  submission against it. A listing-only list would answer "has this seller
   *  already been approached?" with a confident no for a seller invited from a
   *  submission, which is how a second link gets mailed. */
  listSellerPrequalificationInvites: (listingId: string, page = 1, pageSize = 25) =>
    api.get<PagedResult<PrequalInviteDto>>(
      `/listings/${listingId}/seller-prequalification/invites`, { page, pageSize }),
  sendSellerPrequalificationInvite: (
    listingId: string, payload: SendSellerPrequalInvitePayload,
  ) =>
    api.post<PrequalInviteDto>(
      `/listings/${listingId}/seller-prequalification/invites`, payload),
  resendSellerPrequalificationInvite: (listingId: string, inviteId: string) =>
    api.post<PrequalInviteDto>(
      `/listings/${listingId}/seller-prequalification/invites/${inviteId}/resend`, {}),
};

export const buyerSubmissionsApi = {
  list: (params: BuyerSubmissionListParams) =>
    api.get<PagedResult<BuyerSubmissionSummaryDto>>("/buyer-submissions", params),
  stats: (params: BuyerSubmissionListParams) =>
    api.get<BuyerSubmissionStatsDto>("/buyer-submissions/stats", params),
  get: (id: string) => api.get<BuyerSubmissionDetailDto>(`/buyer-submissions/${id}`),

  create: (body: CreateBuyerSubmissionPayload) =>
    api.post<BuyerSubmissionDetailDto>("/buyer-submissions", body),

  /** The offer structure. Bare `undefined` = unchanged. */
  update: (id: string, body: UpdateBuyerSubmissionPayload) =>
    api.patch<BuyerSubmissionDetailDto>(`/buyer-submissions/${id}`, body),

  /** A status change also STAMPS its milestone, server-side and idempotently
   *  (`??=`), so an earlier stamp is never overwritten by a later revisit. */
  setStatus: (id: string, body: SetBuyerSubmissionStatusPayload) =>
    api.post<BuyerSubmissionDetailDto>(`/buyer-submissions/${id}/status`, body),

  /** Has this buyer already been presented to this listing? Any match REFUSES
   *  the present (ruling R1) — the wizard offers RESTORE or OPEN SUBMISSION. */
  duplicateCheck: (params: { buyerLeadId: string; listingId: string }) =>
    api.get<BuyerSubmissionDuplicateCheckDto>(
      "/buyer-submissions/duplicate-check", params),

  /** The standing presentations for a buyer or a listing (R1) — what the
   *  present pickers disable, and why. */
  presentations: (params: { buyerLeadId?: string; listingId?: string }) =>
    api.get<BuyerSubmissionPresentationDto[]>("/buyer-submissions/presentations", params),

  /** PRESENT ALL SELECTED (D14): one request, each listing on its own. */
  bulkPresent: (body: { buyerLeadId: string; listingIds: string[] }) =>
    api.post<BuyerBulkPresentResultDto>("/buyer-submissions/bulk-present", body),

  /** D1: archive (hidden, restorable). Refused once it became a deal (D7). */
  archive: (id: string) =>
    api.post<BuyerSubmissionDetailDto>(`/buyer-submissions/${id}/archive`, {}),

  /** D1: restore exactly as it was. */
  restore: (id: string) =>
    api.post<BuyerSubmissionDetailDto>(`/buyer-submissions/${id}/restore`, {}),

  /** D8: admin-only soft delete of a never-sent submission. */
  remove: (id: string) => api.delete<void>(`/buyer-submissions/${id}`),

  bulkArchive: (submissionIds: string[]) =>
    api.post<BuyerSubmissionBulkResultDto>("/buyer-submissions/bulk-archive", { submissionIds }),

  /** The bulk archive's UNDO (D12). */
  bulkRestore: (submissionIds: string[]) =>
    api.post<BuyerSubmissionBulkResultDto>("/buyer-submissions/bulk-restore", { submissionIds }),

  bulkDelete: (submissionIds: string[]) =>
    api.post<BuyerSubmissionBulkResultDto>("/buyer-submissions/bulk-delete", { submissionIds }),

  /**
   * Records an NDA detail. Every field optional; the server requires at least
   * one, and gates the SIGNATURE on a completed prequalification.
   *
   * <p>🔴 <b>THIS IS THE ONE WRITER OF AN NDA FACT, and the generic
   * `POST /records/{kind}` append is NOT a substitute for it.</b> Three things
   * only `RecordNdaAsync` does:</p>
   *
   * <ol>
   *   <li><b>The authorisation gate.</b> Phoenix signs the listing broker's NDA
   *   on the buyer's behalf, and the authority for that comes from the
   *   prequalification funnel's fine print. `RecordNdaAsync` refuses a
   *   signature without a completed prequalification; the generic append does
   *   not ask. Routing a signature through the append would walk around a
   *   consent gate, silently.</li>
   *   <li><b>The mirror columns.</b> `BuyerPackageService` gates package
   *   generation on `NdaSignedAtUtc`, and the submission list derives its
   *   settled flag from `NdaSignedAtUtc || NdaNotRequiredReason`. The generic
   *   append writes a version and leaves those columns where they were — so a
   *   package gate would stop seeing NDAs recorded after the cut-over.</li>
   *   <li><b>One transaction.</b> It stages the version onto the same
   *   `SaveChanges` as the mirror, so the record and every existing reader can
   *   never disagree.</li>
   * </ol>
   *
   * <p>Recording a REPLACEMENT NDA is therefore this same call with the new
   * signature: the version stack supersedes rather than overwrites, and the
   * mirror follows the head.</p>
   */
  recordNda: (id: string, body: RecordNdaPayload) =>
    api.post<BuyerSubmissionDetailDto>(`/buyer-submissions/${id}/nda`, body),

  /** One paperwork record in full — current version, history, entries, and
   *  whether this caller may append. Never 404s for an empty record: "no NDA on
   *  file" is a state the card renders, not a missing resource. */
  getRecord: (id: string, kind: BuyerRecordKind) =>
    api.get<BuyerRecordDto>(`/buyer-submissions/${id}/records/${kind}`),

  /**
   * Adds a dated, attributed note to a version.
   *
   * <p>Not submission-scoped, deliberately: a version id is enough for the
   * service to resolve the owner, and a submission segment would let the path
   * and the row disagree about which record is being written.</p>
   */
  addRecordEntry: (versionId: string, body: AddBuyerRecordEntryPayload) =>
    api.post<BuyerRecordVersionEntryDto>(
      `/buyer-submissions/records/versions/${versionId}/entries`, body),

  /** Corrects a note. 403 unless the caller wrote it AND it is their most recent
   *  on that version — which is exactly what the entry's `canEdit` reports, so
   *  the control is only ever offered where the server will accept it. */
  updateRecordEntry: (entryId: string, body: UpdateBuyerRecordEntryPayload) =>
    api.put<BuyerRecordVersionEntryDto>(
      `/buyer-submissions/records/entries/${entryId}`, body),

  /** Retracts a note, under the same rule as the edit. */
  deleteRecordEntry: (entryId: string) =>
    api.delete<void>(`/buyer-submissions/records/entries/${entryId}`),

  /** Documents already filed in this submission's folder — the attestation
   *  picker's source. A picker, never an uploader. */
  listDocuments: (id: string) =>
    api.get<DocumentDto[]>(`/buyer-submissions/${id}/documents`),

  /** The Potential Deal Structure, its computed figures and the deterministic
   *  Suggested structure. A pure read: no model call, no write. */
  getDealStructure: (id: string) =>
    api.get<BuyerDealStructureDto>(`/buyer-submissions/${id}/deal-structure`),

  /** Save one wizard step, or the EDIT modal's whole structure. A refused
   *  figure is a 400 carrying the sentence to show; nothing is saved. */
  patchDealStructure: (id: string, body: UpdateBuyerDealStructurePayload) =>
    api.patch<BuyerDealStructureDto>(`/buyer-submissions/${id}/deal-structure`, body),

  // Escalation to Deal Staging (Escalation PR-2). The preview is its own call
  // rather than a field on the detail read, because the gate walks
  // Listing → ListingAgent → Broker → liaison and a GET of a submission should
  // not pay for that every time it is looked at.
  getEscalation: (id: string) =>
    api.get<BuyerSubmissionEscalationPreviewDto>(`/buyer-submissions/${id}/escalation`),
  escalate: (id: string, body: EscalateBuyerSubmissionPayload) =>
    api.post<BuyerSubmissionEscalationResultDto>(
      `/buyer-submissions/${id}/escalation`,
      body,
    ),

  // The SELLER's prequalification (Escalation PR-4). Addressed by the
  // submission — that is where the specialist is standing, and it is what
  // carries the ownership rule — but the LINK lands on the submission's
  // listing, which is shared. Under Submissions.Read / Submissions.Manage;
  // F1/F2 hold no Prequalification.* permission at all.
  getSellerPrequalification: (id: string) =>
    api.get<ListingSellerPrequalificationDto>(
      `/buyer-submissions/${id}/seller-prequalification`),

  /** An empty search returns an empty list: there is no seller contact record
   *  to default from, and answering an unasked search with the whole book is a
   *  directory rather than a suggestion. */
  findSellerPrequalificationCandidates: (id: string, search?: string) =>
    api.get<PrequalificationCandidateDto[]>(
      `/buyer-submissions/${id}/seller-prequalification/candidates`,
      search ? { search } : undefined),

  linkSellerPrequalification: (id: string, submissionId: string) =>
    api.post<ListingSellerPrequalificationDto>(
      `/buyer-submissions/${id}/seller-prequalification`, { submissionId }),
  unlinkSellerPrequalification: (id: string) =>
    api.delete<ListingSellerPrequalificationDto>(
      `/buyer-submissions/${id}/seller-prequalification`),

  listSellerPrequalificationInvites: (id: string, page = 1, pageSize = 25) =>
    api.get<PagedResult<PrequalInviteDto>>(
      `/buyer-submissions/${id}/seller-prequalification/invites`, { page, pageSize }),
  sendSellerPrequalificationInvite: (id: string, payload: SendSellerPrequalInvitePayload) =>
    api.post<PrequalInviteDto>(
      `/buyer-submissions/${id}/seller-prequalification/invites`, payload),
  resendSellerPrequalificationInvite: (id: string, inviteId: string) =>
    api.post<PrequalInviteDto>(
      `/buyer-submissions/${id}/seller-prequalification/invites/${inviteId}/resend`, {}),

  // Package. Generation is its OWN call, never a side effect of sending, and it
  // is addressed PER DOCUMENT — every endpoint here is reached through the
  // submission, so the row-ownership check the server performs always has one.
  getPackage: (id: string) =>
    api.get<BuyerSubmissionPackageDto>(`/buyer-submissions/${id}/package`),

  /** Generates (or regenerates) ONE document. Touches no sibling.
   *
   *  🔴 Regenerating RE-RUNS the AI rather than re-rendering stored prose: a
   *  specialist presses Regenerate because they want different words, and an
   *  edit is how they keep their own. */
  generateDocument: (id: string, kind: BuyerSubmissionDocumentKind) =>
    api.post<BuyerSubmissionPackageDto>(
      `/buyer-submissions/${id}/package/documents/${kind}`),

  /** All four authored documents, cover letter LAST so it reads the others
   *  rather than predicting them. */
  generateAllDocuments: (id: string) =>
    api.post<BuyerSubmissionPackageDto>(`/buyer-submissions/${id}/package/documents`),

  updateDocumentNarrative: (
    id: string,
    kind: BuyerSubmissionDocumentKind,
    body: UpdateBuyerSubmissionDocumentPayload,
  ) =>
    api.put<BuyerSubmissionPackageDto>(
      `/buyer-submissions/${id}/package/documents/${kind}`, body),

  /** Pulls the current prequalification letter version into the package.
   *  Include, refresh, download — never author. */
  refreshPackageLetter: (id: string) =>
    api.post<BuyerSubmissionPackageDto>(`/buyer-submissions/${id}/package/letter/refresh`),

  /** Chooses which prequalification letter this package encloses. A null
   *  `submissionId` CLEARS the override and returns the submission to the
   *  buyer's most recent qualified letter — there is no separate delete verb.
   *  Sets the pointer only; nothing is regenerated, because the letter is not
   *  rendered here in the first place. */
  setPackageLetterOverride: (id: string, submissionId: string | null) =>
    api.put<BuyerSubmissionPackageDto>(
      `/buyer-submissions/${id}/package/letter-override`, { submissionId }),

  /** D8 — turn "Buyer handles broker communications" on or off. Turning it
   *  on is refused (400) when the buyer record has no phone or email. */
  setBuyerHandlesBrokerCommunications: (id: string, enabled: boolean) =>
    api.put<BuyerSubmissionPackageDto>(
      `/buyer-submissions/${id}/package/buyer-handles-broker-communications`, { enabled }),

  /** D5/D6 — this submission's own Referral Disclosure: on with a broker and
   *  a fee, or off. Refused (400) for an archived new pick or a fee outside
   *  (0, 10] / more than two decimals. Nothing is regenerated. */
  setReferralDisclosure: (id: string, body: SetReferralDisclosurePayload) =>
    api.put<BuyerSubmissionPackageDto>(`/buyer-submissions/${id}/package/referral-disclosure`, body),

  /** D5 "Use buyer default": unsent clears the override; sent copies the
   *  buyer's current default in once (ruling Q6). */
  useBuyerDefaultReferralDisclosure: (id: string) =>
    api.delete<BuyerSubmissionPackageDto>(`/buyer-submissions/${id}/package/referral-disclosure`),

  getDocumentPdf: (id: string, kind: BuyerSubmissionDocumentKind) =>
    api.getBlob(`/buyer-submissions/${id}/package/documents/${kind}/pdf`),
  /** The editable Word copy, offered only where `wordAvailable` is true. */
  getDocumentDocx: (id: string, kind: BuyerSubmissionDocumentKind) =>
    api.getBlob(`/buyer-submissions/${id}/package/documents/${kind}/docx`),

  getPackageDownload: (id: string) =>
    api.get<BuyerPackageDownloadDto>(`/buyer-submissions/${id}/package/download`),
  sendPackage: (id: string, body: SendBuyerPackagePayload) =>
    api.post<SendBuyerPackageResultDto>(`/buyer-submissions/${id}/package/send`, body),
  markPackageSent: (id: string) =>
    api.post<SendBuyerPackageResultDto>(`/buyer-submissions/${id}/package/mark-sent`),

  // The same package, sent to the buyer. Same document, buyer-addressed cover.
  getPackageBuyerDraft: (id: string) =>
    api.get<BuyerPackageBuyerDraftDto>(`/buyer-submissions/${id}/package/buyer-draft`),
  sendPackageToBuyer: (id: string, body: SendBuyerPackageToBuyerPayload) =>
    api.post<SendBuyerPackageResultDto>(
      `/buyer-submissions/${id}/package/send-to-buyer`, body),
  markPackageSentToBuyer: (id: string) =>
    api.post<SendBuyerPackageResultDto>(
      `/buyer-submissions/${id}/package/mark-sent-to-buyer`),

  // Listing intro email — to the BUYER, not the listing broker.
  getIntroEmail: (id: string) =>
    api.get<BuyerIntroEmailStateDto>(`/buyer-submissions/${id}/intro-email`),

  /** Drafts, or redrafts. Its OWN call, never a side effect of sending — which
   *  is what makes the review step real: the AI's words cannot reach a buyer
   *  without a specialist having had them on screen first. 422 when the daily
   *  AI cost ceiling is breached. */
  generateIntroEmail: (id: string) =>
    api.post<BuyerIntroEmailStateDto>(`/buyer-submissions/${id}/intro-email`),

  updateIntroEmail: (id: string, body: UpdateBuyerIntroEmailPayload) =>
    api.put<BuyerIntroEmailStateDto>(`/buyer-submissions/${id}/intro-email`, body),

  /** The body is optional on the server: omitting it sends exactly as a
   *  bodyless POST always did, with no listing-page line. */
  sendIntroEmail: (id: string, body?: SendBuyerIntroEmailPayload) =>
    api.post<SendBuyerIntroEmailResultDto>(`/buyer-submissions/${id}/intro-email/send`, body),

  /**
   * ⚠️ A GET that WRITES. Taking the copy away IS the send: it stamps
   * `IntroEmailSentAtUtc` exactly as the Phoenix path does, so a specialist who
   * prefers their own mailbox is not invisible in the funnel. Never call it
   * speculatively, never from a `useQuery`, and never on a retry — every call
   * records a contact. It is gated `Submissions.Manage`, unlike the package's
   * download companion, which stamps nothing.
   */
  // `includeListingLink` rides the query string only when it is on, so a copy
  // taken with it off makes exactly the request it always made.
  getIntroEmailDownload: (id: string, includeListingLink = false) =>
    api.get<BuyerIntroEmailDownloadDto>(
      `/buyer-submissions/${id}/intro-email/download`,
      includeListingLink ? { includeListingLink: true } : undefined,
    ),

  // ---- Broker intro email ------------------------------------------------
  //
  // The sibling above goes to the BUYER; these go to the LISTING BROKER. Every
  // name here says "broker" for that reason — a specialist must never be one
  // mis-click from writing to the wrong audience, and neither must a caller.

  getBrokerIntro: (id: string) =>
    api.get<BrokerIntroEmailStateDto>(`/buyer-submissions/${id}/broker-intro`),

  /** Drafts or redrafts. Its OWN call — never a side effect of sending, so no
   *  composed sentence about a buyer reaches an outside broker without a
   *  specialist having had it on screen. */
  generateBrokerIntro: (id: string) =>
    api.post<BrokerIntroEmailStateDto>(`/buyer-submissions/${id}/broker-intro`),

  updateBrokerIntro: (id: string, body: UpdateBrokerIntroEmailPayload) =>
    api.put<BrokerIntroEmailStateDto>(`/buyer-submissions/${id}/broker-intro`, body),

  sendBrokerIntro: (id: string) =>
    api.post<SendBrokerIntroEmailResultDto>(`/buyer-submissions/${id}/broker-intro/send`),

  /**
   * COPY FOR MY OWN EMAIL. 🔴 A READ since R4 — it records nothing. The send is
   * recorded by `recordBrokerIntroSend`, a separate click after the consultant
   * has actually sent the email.
   */
  getBrokerIntroDownload: (id: string) =>
    api.get<BrokerIntroEmailDownloadDto>(`/buyer-submissions/${id}/broker-intro/download`),

  /**
   * RECORD SEND (R4): the exact subject and body COPY placed on the clipboard.
   * Writes the send version, the sent columns and one communications entry
   * (buyer, submission and listing). Rate-limited server-side (429).
   */
  recordBrokerIntroSend: (id: string, body: { subject: string; body: string }) =>
    api.post<BrokerIntroEmailStateDto>(`/buyer-submissions/${id}/broker-intro/record-send`, body),
};

export interface SendBuyerLeadsToTeamRequest {
  buyerLeadIds: string[];
  buyerTeamId: string;
  reason?: string;
}

export interface SendToTeamItemDto {
  buyerLeadId: string;
  moved: boolean;
  reason: string | null;
}

export interface SendToTeamResultDto {
  moved: number;
  skipped: number;
  items: SendToTeamItemDto[];
}

export const buyerAssignmentsApi = {
  queue: (params: BuyerAssignmentQueueParams) =>
    api.get<BuyerAssignmentQueueDto>("/buyer-assignments/queue", params),

  /** Who the caller may hand a buyer to — for one buyer, or (no id) for a
   *  buyer about to be created. Gated any-of `Buyers.ManageAll,Buyers.Update`:
   *  an owner may reach it for their own buyer. */
  candidates: (buyerLeadId?: string | null) =>
    api.get<BuyerSpecialistCandidatesDto>(
      "/buyer-assignments/candidates",
      buyerLeadId ? { buyerLeadId } : undefined,
    ),
  /** Who the caller may hand an existing buyer to — read once per caller, not
   *  per buyer. Same door as `candidates`. */
  handOffRoster: () => api.get<BuyerHandOffRosterDto>("/buyer-assignments/hand-off-roster"),
  /** Place ONE named buyer with ONE named specialist. Distinct from
   *  `bulkAssign` (a checkbox selection) and `autoDistribute` (fan the whole
   *  queue out); this is the deliberate placement a manager makes by hand. */
  assign: (buyerLeadId: string, body: AssignBuyerPayload) =>
    api.post<BuyerLeadSummaryDto>(`/buyer-assignments/${buyerLeadId}/assign`, body),

  /** Returns the buyer to the unassigned queue. */
  unassign: (buyerLeadId: string, body: { reason?: string | null } = {}) =>
    api.post<BuyerLeadSummaryDto>(`/buyer-assignments/${buyerLeadId}/unassign`, body),

  // ⚠️ `GET /buyer-assignments/grants` (the assignment audit trail) is
  // DELIBERATELY not wired here. Its query key exists and its endpoint works,
  // but no surface in this PR's scope reads it, and adding a client function
  // nothing calls would manufacture exactly the orphan class this PR exists to
  // remove. The DTO and params types above are declared so the eventual
  // consumer starts from a mirror that is already correct.
  bulkAssign: (body: BulkAssignBuyersPayload) =>
    api.post<BulkAssignResultDto>("/buyer-assignments/bulk-assign", body),

  /** Fan the unassigned queue out across eligible specialists. Call with
   *  `preview: true` first — the plan is what a manager confirms, and the run is
   *  recomputed server-side when it is applied. */
  autoDistribute: (body: BuyerAutoDistributePayload) =>
    api.post<BuyerAutoDistributeResultDto>("/buyer-assignments/auto-distribute", body),

  /** 🔴 Buyers.ManageAllTeams, enforced SERVER-SIDE and re-checked in the
   *  service — hiding the control is not the gate. Rewrites the TEAM only; the
   *  assigned specialist is untouched, so a lead being worked keeps its owner
   *  even when they sit on another team (D2). */
  sendToTeam: (body: SendBuyerLeadsToTeamRequest) =>
    api.post<SendToTeamResultDto>("/buyer-assignments/send-to-team", body),
};

export const buyerAllocationApi = {
  settings: () => api.get<BuyerAllocationSettingsDto>("/buyer-allocation/settings"),

  /** An all-null body resets the global policy to the shipped defaults. */
  saveGlobal: (body: SaveBuyerAllocationPayload) =>
    api.put<BuyerAllocationSettingsDto>("/buyer-allocation/settings", body),

  /** An all-null body reverts this specialist to the global policy. */
  saveOverride: (specialistUserId: string, body: SaveBuyerAllocationPayload) =>
    api.put<BuyerAllocationSettingsDto>(
      `/buyer-allocation/settings/${encodeURIComponent(specialistUserId)}`,
      body,
    ),
  clearOverride: (specialistUserId: string) =>
    api.delete<BuyerAllocationSettingsDto>(
      `/buyer-allocation/settings/${encodeURIComponent(specialistUserId)}`,
    ),

  /** ONE team's tier. An all-null body reverts the team to the global policy.
   *  The endpoints predate this client method — they had no caller until the
   *  team ALLOCATION tab (Q8 A). */
  saveTeamTier: (buyerTeamId: string, body: SaveBuyerAllocationPayload) =>
    api.put<BuyerAllocationSettingsDto>(
      `/buyer-allocation/settings/teams/${buyerTeamId}`,
      body,
    ),
  clearTeamTier: (buyerTeamId: string) =>
    api.delete<BuyerAllocationSettingsDto>(`/buyer-allocation/settings/teams/${buyerTeamId}`),

  /** Standings for the specialists the caller dispatches. `buyerTeamId` NARROWS
   *  an all-teams caller to one team's roster; for anybody else it is ignored —
   *  their own team stands (the `BuyerTeamScopeResult.Narrow` rule). */
  entitlements: (params?: { buyerTeamId?: string }) =>
    api.get<BuyerEntitlementDto[]>("/buyer-allocation/entitlements", params),
  myEntitlement: () => api.get<BuyerEntitlementDto>("/buyer-allocation/entitlements/me"),
};

// ---------------------------------------------------------------------------
// Combined intro email — the CLIENT. Keyed on the BUYER, not on a submission:
// one email covers several listings, so no one listing's route can own it. The
// single intro keeps its routes on `buyerSubmissionsApi` and is untouched.
// ---------------------------------------------------------------------------

/** The `details.code` a draft refusal carries when the buyer already has an
 *  unsent combined draft that the request did not name as the one it replaces.
 *  Branch on this CODE, never on the prose. Mirrors
 *  `BuyerCombinedIntroDraftExistsException.ErrorCode`. */
export const COMBINED_INTRO_DRAFT_EXISTS_CODE = "combinedIntroDraftExists";

/** The unsent draft a refused draft request would have replaced, or null when
 *  the error is anything else. */
export function combinedIntroExistingDraft(
  e: unknown,
): { draftId: string; listingCount: number } | null {
  if (!(e instanceof ApiError)) return null;
  const d = e.details as
    | { code?: unknown; draftId?: unknown; listingCount?: unknown }
    | null
    | undefined;
  if (d?.code !== COMBINED_INTRO_DRAFT_EXISTS_CODE || typeof d.draftId !== "string") return null;
  return {
    draftId: d.draftId,
    listingCount: typeof d.listingCount === "number" ? d.listingCount : 0,
  };
}

export const buyerCombinedIntroApi = {
  /** The buyer's open draft, or null — what the tab reads to offer "Resume
   *  draft". */
  getOpenDraft: (buyerId: string) =>
    api.get<BuyerCombinedIntroEmailDto | null>(`/buyers/${buyerId}/combined-intro-email`),

  get: (buyerId: string, id: string) =>
    api.get<BuyerCombinedIntroEmailDto>(`/buyers/${buyerId}/combined-intro-email/${id}`),

  /** Drafts, or redrafts. Its OWN call, never a side effect of sending. A 400
   *  carrying `COMBINED_INTRO_DRAFT_EXISTS_CODE` means ask first, then resend
   *  with `replaceDraftId`. */
  draft: (buyerId: string, body: DraftBuyerCombinedIntroPayload) =>
    api.post<BuyerCombinedIntroEmailDto>(`/buyers/${buyerId}/combined-intro-email`, body),

  update: (buyerId: string, id: string, body: UpdateBuyerCombinedIntroPayload) =>
    api.put<BuyerCombinedIntroEmailDto>(`/buyers/${buyerId}/combined-intro-email/${id}`, body),

  send: (buyerId: string, id: string, body: SendBuyerCombinedIntroPayload) =>
    api.post<SendBuyerCombinedIntroResultDto>(
      `/buyers/${buyerId}/combined-intro-email/${id}/send`,
      body,
    ),

  /**
   * ⚠️ WRITES. Taking the copy away IS the send — it records exactly what a
   * Phoenix send records, which is why it is a POST. Never call it
   * speculatively and never on a retry: every call records a contact.
   */
  copy: (buyerId: string, id: string, body: SendBuyerCombinedIntroPayload) =>
    api.post<BuyerCombinedIntroCopyDto>(
      `/buyers/${buyerId}/combined-intro-email/${id}/copy`,
      body,
    ),
};
